/* eslint-disable react/react-in-jsx-scope */
import DocumentTypeCombobox from "@/components/custom/DocumentTypeCombobox";
import FileDropzone from "@/components/custom/FileDropzone";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody } from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/auth/hooks/useAuth";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import {
  convertEuroToCurrency,
  convertToEuro,
  formatRateDateLabel,
} from "@/libs/amount";
import { isImage } from "@/libs/utils";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileSignature,
  Loader2,
  Lock,
  Paperclip,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AddFunderDialog from "../AddFunderDialog";
import ContactAdd from "../ContactAdd";
import AddFundingRow from "./AddFundingRow";
import FundingMonthlyMatrix from "./FundingMonthlyMatrix";
import FundingRow from "./FundingRow";
import FundingSkeleton from "./FundingSkeleton";
import FundingTrackingIntroPanel, {
  CalendarDetailItem,
  CalendarEventInput,
  FundingReminderItem,
  IntroStat,
} from "./FundingTrackingIntroPanel";
import FundingTrackingTutorial from "./FundingTrackingTutorial";
import {
  fetchHexaRateRateToEuroAtDateDetailed,
  fetchHexaRateRateToEuroAtDate,
  readRealFundingContractDateRaw,
  readRealFundingDateUsesFallback,
  resolveCurrentRateInfoWithTodayApi,
  resolveHistoricalRateToEuroAtDate,
} from "./realFundingHistoricalRate";

/** ===================== Utils (currencies desired) ===================== */

function toNumber(v: any): number {
  const n = Number(v ?? 0);
  return !n || Number.isNaN(n) ? 0 : n;
}

function parseInputNumber(v: any): number {
  const n = Number(
    String(v ?? "")
      .replace(/\s/g, "")
      .replace(",", "."),
  );
  return !n || Number.isNaN(n) ? 0 : n;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const MONEY_COMPARISON_EPSILON = 0.005;

function readRealFundingConvertedAmount(funding: any): number | null {
  const n = Number(
    funding?.paidAmountConverted ??
      funding?.paid_amount_converted ??
      funding?.paid_amountConverted ??
      funding?.paidamountconverted,
  );
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readRealFundingRateToEuro(funding: any): number | null {
  const amountEur = Number(funding?.paidAmountEUR ?? funding?.paidAmount);
  if (!Number.isFinite(amountEur) || amountEur <= 0) return null;

  const amountConverted = readRealFundingConvertedAmount(funding);
  if (!amountConverted) return null;

  const rate = amountEur / amountConverted;
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

function normalizeCollectionLike(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const values = Object.values(value);
  if (!values.length) return [];

  const looksLikeEntityCollection = values.every(
    (item) => item && typeof item === "object",
  );
  return looksLikeEntityCollection ? (values as any[]) : [];
}

function readCodeFromCurrencyLike(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    const code = value.trim().toUpperCase();
    return code || null;
  }
  if (typeof value === "object") {
    const code = String(value?.code ?? value?.currencyCode ?? "")
      .trim()
      .toUpperCase();
    return code || null;
  }
  return null;
}

function readIdFromCurrencyLike(value: any): number | null {
  const raw =
    typeof value === "object"
      ? value?.id
      : typeof value === "number"
        ? value
        : null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readFundingInputCurrencyCode(
  funding: any,
  projectCurrency?: any,
): string | null {
  const obtainedRows = normalizeCollectionLike(
    funding?.obtainedFundings ??
      funding?.obtainedFunding ??
      funding?.obtained_fundings ??
      funding?.obtained_funding,
  );

  for (const row of obtainedRows) {
    const code =
      readCodeFromCurrencyLike(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount,
      ) ??
      (Number(
        row?.currency ??
          row?.devise ??
          row?.currencySelected ??
          row?.deviseSelected ??
          row?.paidDevise ??
          row?.estimatedDevise ??
          row?.deviseAmount ??
          row?.currencyAmount,
      ) === 3
        ? "EUR"
        : null);
    if (code) return code;
  }

  const directCode =
    readCodeFromCurrencyLike(funding?.paidDevise) ??
    readCodeFromCurrencyLike(funding?.paid_devise) ??
    readCodeFromCurrencyLike(funding?.devise);
  if (directCode) return directCode;

  const directId =
    readIdFromCurrencyLike(funding?.paidDevise) ??
    readIdFromCurrencyLike(funding?.paid_devise) ??
    readIdFromCurrencyLike(funding?.devise);
  if (directId === 3) return "EUR";

  const projectCurrencyId = readIdFromCurrencyLike(projectCurrency);
  const projectCurrencyCode = readCodeFromCurrencyLike(projectCurrency);
  if (
    directId &&
    projectCurrencyId &&
    directId === projectCurrencyId &&
    projectCurrencyCode
  ) {
    return projectCurrencyCode;
  }

  return null;
}

function readCurrencyLike(df: any, projectCurrency: any) {
  // ✅ essaye tous les noms possibles côté desiredFunding
  const raw =
    df?.currency ??
    df?.devise ??
    df?.currencySelected ??
    df?.deviseSelected ??
    df?.estimatedDevise ??
    df?.paidDevise ??
    df?.deviseAmount ??
    df?.currencyAmount ??
    null;

  // objet devise complet
  if (raw && typeof raw === "object") return raw;

  // string code (ex: "USD")
  if (typeof raw === "string" && raw.trim()) {
    const code = raw.trim().toUpperCase();
    if (projectCurrency?.code?.toUpperCase() === code) return projectCurrency;
    return { code };
  }

  // numeric (id devise)
  if (typeof raw === "number") {
    // heuristique : si c’est la devise projet
    if (projectCurrency?.id && Number(projectCurrency.id) === Number(raw)) {
      return projectCurrency;
    }
    // heuristique : si 3 = EUR dans ton référentiel (comme tu utilises estimatedDevise:3)
    if (Number(raw) === 3) return { code: "EUR" };
    return null;
  }

  // fallback: si rien -> on suppose "legacy" en EUR
  return null;
}

async function desiredAmountToEUR(
  df: any,
  projectCurrency: any,
  options?: { projectRateToEuro?: number | null },
): Promise<number> {
  const amountRaw = toNumber(df?.amount ?? df?.value ?? df?.montant);
  if (!amountRaw) return 0;

  const currency = readCurrencyLike(df, projectCurrency);

  // si pas de devise indiquée => on suppose que c'est déjà EUR (legacy)
  if (!currency) return amountRaw;

  const code = String(currency?.code ?? "EUR").toUpperCase();
  if (code === "EUR") return amountRaw;
  const projectCode = String(projectCurrency?.code ?? "").toUpperCase();
  const projectRateToEuro = Number(options?.projectRateToEuro ?? 0);
  if (
    projectCode &&
    code === projectCode &&
    Number.isFinite(projectRateToEuro) &&
    projectRateToEuro > 0
  ) {
    return toNumber(amountRaw * projectRateToEuro);
  }

  try {
    // convertToEuro peut être sync ou async selon ton implémentation => Promise.resolve
    const eur = await Promise.resolve(convertToEuro(amountRaw, currency));
    return toNumber(eur);
  } catch {
    // fallback safe
    return amountRaw;
  }
}

function currencyLabel(cur: any) {
  if (!cur) return "€";
  const code = String(cur?.code ?? "").toUpperCase();
  const sym = cur?.symbol;
  if (sym) return sym;
  if (code === "EUR") return "€";
  return code || "€";
}

function formatMoney(n: number, cur: any) {
  return `${(n || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currencyLabel(cur)}`;
}

function formatEuroStatAmount(amount: number) {
  return `${round2(amount).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} €`;
}

function readFundingAmountEur(funding: any): number {
  const amount = Number(
    funding?.paidAmountEUR ??
      funding?.paid_amount_eur ??
      funding?.paidAmount ??
      funding?.paid_amount ??
      0,
  );
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function readObtainedFundingAmountEur(obtainedFunding: any): number {
  const amount = Number(
    obtainedFunding?.amountEUR ??
      obtainedFunding?.amountEur ??
      obtainedFunding?.amount_eur ??
      obtainedFunding?.amountConverted ??
      obtainedFunding?.amount_converted ??
      obtainedFunding?.amount ??
      0,
  );
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function readObtainedFundingRealFundingId(obtainedFunding: any): number | null {
  const raw =
    obtainedFunding?.realFundingId ??
    obtainedFunding?.real_funding_id ??
    obtainedFunding?.realFunding ??
    obtainedFunding?.real_funding ??
    obtainedFunding?.realFunding?.id ??
    null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function sumProjectContractAmountEur(project: any): number {
  const fundings = readProjectFundings(project);
  const realFundingIds = new Set(
    fundings
      .map((funding) => Number(funding?.id))
      .filter((id) => Number.isFinite(id) && id > 0),
  );
  const fundingsTotal = fundings.reduce(
    (sum, funding) => sum + readFundingAmountEur(funding),
    0,
  );
  const obtainedTotal = readProjectObtainedFundings(project).reduce(
    (sum, obtainedFunding) => {
      const linkedRealFundingId =
        readObtainedFundingRealFundingId(obtainedFunding);
      if (linkedRealFundingId && realFundingIds.has(linkedRealFundingId)) {
        return sum;
      }
      return sum + readObtainedFundingAmountEur(obtainedFunding);
    },
    0,
  );
  return round2(fundingsTotal + obtainedTotal);
}

function sumProjectExpectedPaymentsEur(project: any): number {
  return round2(
    readProjectFundings(project).reduce((sum, funding) => {
      const expectedTotal = normalizeCollectionLike(
        funding?.realFundingExpectedPaids,
      ).reduce((innerSum, paid) => {
        const amount = Number(paid?.amount ?? 0);
        return Number.isFinite(amount) && amount > 0 ? innerSum + amount : innerSum;
      }, 0);
      return sum + expectedTotal;
    }, 0),
  );
}

function sumProjectReceivedPaymentsEur(project: any): number {
  return round2(
    readProjectFundings(project).reduce((sum, funding) => {
      const paids = normalizeCollectionLike(funding?.realFundingPaids);
      if (paids.length > 0) {
        const paidTotal = paids.reduce((innerSum, paid) => {
          const amount = Number(paid?.amount ?? 0);
          return Number.isFinite(amount) && amount > 0 ? innerSum + amount : innerSum;
        }, 0);
        return sum + paidTotal;
      }

      const expectedReceivedTotal = normalizeCollectionLike(
        funding?.realFundingExpectedPaids,
      ).reduce((innerSum, paid) => {
        const amount = Number(paid?.amount ?? 0);
        return Number(paid?.status) === 2 && Number.isFinite(amount) && amount > 0
          ? innerSum + amount
          : innerSum;
      }, 0);

      return sum + expectedReceivedTotal;
    }, 0),
  );
}

function sumProjectTerrainPaymentsEur(indicators: any[]): number {
  return round2(
    normalizeCollectionLike(indicators).reduce((sum, indicator) => {
      const type = Number(indicator?.type ?? 0);
      if (type !== 2) return sum;
      const amount = Number(indicator?.valueDisplay ?? indicator?.value ?? 0);
      return Number.isFinite(amount) && amount > 0 ? sum + amount : sum;
    }, 0),
  );
}

type ProjectTrackingFilterKey =
  | "needs"
  | "contracts"
  | "contracts_verdeo"
  | "contracts_internal"
  | "planned"
  | "received"
  | "terrain"
  | "all";

function readProjectFundings(project: any): any[] {
  return normalizeCollectionLike(project?.realFundings);
}

function countFundingsMissingContractDate(fundings: any[]): number {
  return (fundings || []).reduce((sum, funding) => {
    if (readFundingIsVerdeo(funding)) return sum;
    return readRealFundingDateUsesFallback(funding) ? sum + 1 : sum;
  }, 0);
}

function readProjectObtainedFundings(project: any): any[] {
  return normalizeCollectionLike(project?.obtainedFunding);
}

function hasProjectNeeds(project: any): boolean {
  return normalizeCollectionLike(project?.desiredFunding).length > 0;
}

function readFundingIsVerdeo(funding: any): boolean {
  const raw = funding?.verdeo ?? funding?.viaVerdeo ?? funding?.isVerdeo;
  return raw === true || raw === 1 || raw === "1";
}

function readObtainedFundingIsVerdeo(obtainedFunding: any): boolean {
  const raw =
    obtainedFunding?.foundByVerdeo ??
    obtainedFunding?.viaVerdeo ??
    obtainedFunding?.verdeo;
  return raw === true || raw === 1 || raw === "1";
}

function readObtainedFundingDate(obtainedFunding: any): string | Date | null {
  return (
    obtainedFunding?.obtainedDate ??
    obtainedFunding?.date ??
    obtainedFunding?.createdAt ??
    null
  );
}

function hasProjectContracts(project: any): boolean {
  return (
    readProjectFundings(project).length > 0 ||
    readProjectObtainedFundings(project).length > 0
  );
}

function hasProjectVerdeoContracts(project: any): boolean {
  return (
    readProjectFundings(project).some((funding) =>
      readFundingIsVerdeo(funding),
    ) ||
    readProjectObtainedFundings(project).some((obtainedFunding) =>
      readObtainedFundingIsVerdeo(obtainedFunding),
    )
  );
}

function hasProjectInternalContracts(project: any): boolean {
  return (
    readProjectFundings(project).some(
      (funding) => !readFundingIsVerdeo(funding),
    ) ||
    readProjectObtainedFundings(project).some(
      (obtainedFunding) => !readObtainedFundingIsVerdeo(obtainedFunding),
    )
  );
}

function hasProjectPlannedPayments(project: any): boolean {
  return readProjectFundings(project).some((funding) =>
    normalizeCollectionLike(funding?.realFundingExpectedPaids).some((paid) => {
      const amount = Number(paid?.amount ?? 0);
      return Number.isFinite(amount) && amount > 0;
    }),
  );
}

function hasProjectReceivedPayments(project: any): boolean {
  return readProjectFundings(project).some((funding) => {
    const expectedReceived = normalizeCollectionLike(
      funding?.realFundingExpectedPaids,
    ).some((paid) => {
      const amount = Number(paid?.amount ?? 0);
      return (
        Number(paid?.status) === 2 && Number.isFinite(amount) && amount > 0
      );
    });
    if (expectedReceived) return true;
    return normalizeCollectionLike(funding?.realFundingPaids).some((paid) => {
      const amount = Number(paid?.amount ?? 0);
      return Number.isFinite(amount) && amount > 0;
    });
  });
}

function hasProjectTerrainPayments(
  project: any,
  projectIndicatorsById: Record<number, any[]>,
): boolean {
  const projectId = Number(project?.id);
  if (!projectId || Number.isNaN(projectId)) return false;
  const indicators = projectIndicatorsById[projectId];
  return Array.isArray(indicators) && indicators.length > 0;
}

function keepProjectByTrackingFilter(
  filter: ProjectTrackingFilterKey,
  project: any,
  projectIndicatorsById: Record<number, any[]>,
) {
  switch (filter) {
    case "needs":
      return hasProjectNeeds(project);
    case "contracts_verdeo":
      return hasProjectVerdeoContracts(project);
    case "contracts_internal":
      return hasProjectInternalContracts(project);
    case "planned":
      return hasProjectPlannedPayments(project);
    case "received":
      return hasProjectReceivedPayments(project);
    case "terrain":
      return hasProjectTerrainPayments(project, projectIndicatorsById);
    case "all":
    default:
      return true;
  }
}

function collectProjectCalendarEvents(project: any): CalendarEventInput[] {
  const events: CalendarEventInput[] = [];
  const projectId = Number(project?.id);
  const projectName = String(project?.name || `Projet #${projectId || "?"}`);
  const targetId = projectId ? `funding-project-card-${projectId}` : undefined;
  for (const funding of readProjectFundings(project)) {
    const contractDate = readRealFundingContractDateRaw(funding);
    if (contractDate) {
      events.push({
        date: contractDate,
        label: readFundingIsVerdeo(funding)
          ? `${projectName} · Contrat via Verdeo`
          : `${projectName} · Contrat interne`,
        targetId,
        projectId,
      });
    }

    for (const paid of normalizeCollectionLike(
      funding?.realFundingExpectedPaids,
    )) {
      if (paid?.at) {
        events.push({
          date: paid.at,
          label: `${projectName} · Versement prévu`,
          targetId,
          projectId,
        });
      }
      if (paid?.datePaid) {
        events.push({
          date: paid.datePaid,
          label: `${projectName} · Versement reçu`,
          targetId,
          projectId,
        });
      }
    }
    for (const paid of normalizeCollectionLike(funding?.realFundingPaids)) {
      if (paid?.at) {
        events.push({
          date: paid.at,
          label: `${projectName} · Versement reçu`,
          targetId,
          projectId,
        });
      }
    }
  }
  for (const obtainedFunding of readProjectObtainedFundings(project)) {
    const obtainedDate = readObtainedFundingDate(obtainedFunding);
    if (obtainedDate) {
      events.push({
        date: obtainedDate,
        label: readObtainedFundingIsVerdeo(obtainedFunding)
          ? `${projectName} · Contrat via Verdeo`
          : `${projectName} · Contrat interne`,
        targetId,
        projectId,
      });
    }
  }
  return events;
}

function toValidDate(value: any): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDayStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toDayKey(date: Date) {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isExpectedPaidAlreadyReceived(paid: any) {
  if (Number(paid?.status) === 2) return true;
  if (paid?.datePaid) return true;
  return false;
}

function readProjectPendingVerdeoAllocationEur(project: any): number {
  const directCandidates = [
    project?.fundingTraceability?.verdeoPendingAllocationFsEur,
    project?.fundingTraceability?.pendingVerdeoAllocationFsEur,
    project?.verdeoPendingAllocationFsEur,
    project?.pendingVerdeoAllocationFsEur,
    project?.verdeo_pending_allocation_fs_eur,
  ];

  for (const candidate of directCandidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0.5) return n;
  }

  const invoices = normalizeCollectionLike(project?.verdeoFundingInvoiceds);
  if (!invoices.length) return 0;

  const invoicedFsTotalEur = invoices.reduce((sum, invoice) => {
    const fs = Number(invoice?.fs ?? 0);
    return Number.isFinite(fs) && fs > 0 ? sum + fs : sum;
  }, 0);
  if (invoicedFsTotalEur <= 0.5) return 0;

  const allocatedApproxEur = readProjectObtainedFundings(project).reduce(
    (sum, obtainedFunding) => {
      if (!readObtainedFundingIsVerdeo(obtainedFunding)) return sum;

      const eurValue = Number(
        obtainedFunding?.amountEUR ??
          obtainedFunding?.amountEur ??
          obtainedFunding?.amount_eur ??
          obtainedFunding?.amountConverted ??
          obtainedFunding?.amount_converted ??
          0,
      );
      if (Number.isFinite(eurValue) && eurValue > 0) {
        return sum + eurValue;
      }
      return sum;
    },
    0,
  );

  const pending = invoicedFsTotalEur - allocatedApproxEur;
  return pending > 0.5 ? pending : 0;
}

function collectProjectReminderItems(
  project: any,
  referenceDate: Date = new Date(),
): FundingReminderItem[] {
  const items: FundingReminderItem[] = [];
  const seen = new Set<string>();
  const today = toDayStart(referenceDate);
  const upcomingLimit = new Date(today);
  upcomingLimit.setDate(upcomingLimit.getDate() + 7);

  const projectId = Number(project?.id);
  const projectName = String(project?.name || `Projet #${projectId || "?"}`);
  const targetId = projectId ? `funding-project-card-${projectId}` : undefined;

  const pendingVerdeoAllocationEur =
    readProjectPendingVerdeoAllocationEur(project);

  const pushItem = (
    section: FundingReminderItem["section"],
    label: string,
    date: Date,
  ) => {
    const day = toDayStart(date);
    const id = [
      section,
      targetId || "none",
      projectId || "none",
      toDayKey(day),
      label,
    ].join("|");
    if (seen.has(id)) return;
    seen.add(id);
    items.push({
      id,
      section,
      label,
      date: day,
      targetId,
      projectId: Number.isFinite(projectId) ? projectId : undefined,
    });
  };

  if (pendingVerdeoAllocationEur > 0.5) {
    pushItem("overdue", `${projectName} · Répartition Verdeo à faire`, today);
  }

  const missingContractDateCount = countFundingsMissingContractDate(
    readProjectFundings(project),
  );
  if (missingContractDateCount > 0) {
    pushItem(
      "overdue",
      `${projectName} · ${
        missingContractDateCount > 1
          ? `${missingContractDateCount} dates de contrat à renseigner`
          : "Date du contrat à renseigner"
      }`,
      today,
    );
  }

  const classifyForTodayOrUpcoming = (dateValue: any) => {
    const date = toValidDate(dateValue);
    if (!date) return null;
    const day = toDayStart(date);
    const dayTs = day.getTime();
    if (dayTs === today.getTime()) return { section: "today" as const, day };
    if (dayTs > today.getTime() && dayTs <= upcomingLimit.getTime()) {
      return { section: "upcoming" as const, day };
    }
    return null;
  };

  for (const funding of readProjectFundings(project)) {
    const contract = classifyForTodayOrUpcoming(
      readRealFundingContractDateRaw(funding),
    );
    if (contract) {
      pushItem(
        contract.section,
        readFundingIsVerdeo(funding)
          ? `${projectName} · Contrat via Verdeo`
          : `${projectName} · Contrat interne`,
        contract.day,
      );
    }

    for (const paid of normalizeCollectionLike(
      funding?.realFundingExpectedPaids,
    )) {
      if (isExpectedPaidAlreadyReceived(paid)) continue;
      const amount = Number(paid?.amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) continue;

      const plannedAt = toValidDate(paid?.at);
      if (!plannedAt) continue;
      const plannedDay = toDayStart(plannedAt);
      const plannedTs = plannedDay.getTime();

      if (plannedTs < today.getTime()) {
        pushItem("overdue", `${projectName} · Versement prévu`, plannedDay);
        continue;
      }
      if (plannedTs === today.getTime()) {
        pushItem("today", `${projectName} · Versement prévu`, plannedDay);
        continue;
      }
      if (plannedTs <= upcomingLimit.getTime()) {
        pushItem("upcoming", `${projectName} · Versement prévu`, plannedDay);
      }
    }
  }

  for (const obtainedFunding of readProjectObtainedFundings(project)) {
    const obtained = classifyForTodayOrUpcoming(
      readObtainedFundingDate(obtainedFunding),
    );
    if (!obtained) continue;
    pushItem(
      obtained.section,
      readObtainedFundingIsVerdeo(obtainedFunding)
        ? `${projectName} · Contrat via Verdeo`
        : `${projectName} · Contrat interne`,
      obtained.day,
    );
  }

  return items;
}

function collectProjectReminderItemsFromCalendarEvents(
  project: any,
  events: CalendarEventInput[],
  referenceDate: Date = new Date(),
): FundingReminderItem[] {
  const items: FundingReminderItem[] = [];
  const seen = new Set<string>();
  const today = toDayStart(referenceDate);
  const upcomingLimit = new Date(today);
  upcomingLimit.setDate(upcomingLimit.getDate() + 7);

  const projectId = Number(project?.id);
  const projectName = String(project?.name || `Projet #${projectId || "?"}`);
  const targetId = projectId ? `funding-project-card-${projectId}` : undefined;
  const pendingVerdeoAllocationEur =
    readProjectPendingVerdeoAllocationEur(project);

  const pushItem = (
    section: FundingReminderItem["section"],
    label: string,
    date: Date,
  ) => {
    const day = toDayStart(date);
    const id = [
      section,
      targetId || "none",
      projectId || "none",
      toDayKey(day),
      label,
    ].join("|");
    if (seen.has(id)) return;
    seen.add(id);
    items.push({
      id,
      section,
      label,
      date: day,
      targetId,
      projectId: Number.isFinite(projectId) ? projectId : undefined,
    });
  };

  if (pendingVerdeoAllocationEur > 0.5) {
    pushItem("overdue", `${projectName} · Répartition Verdeo à faire`, today);
  }

  const missingContractDateCount = countFundingsMissingContractDate(
    readProjectFundings(project),
  );
  if (missingContractDateCount > 0) {
    pushItem(
      "overdue",
      `${projectName} · ${
        missingContractDateCount > 1
          ? `${missingContractDateCount} dates de contrat à renseigner`
          : "Date du contrat à renseigner"
      }`,
      today,
    );
  }

  for (const event of events || []) {
    if (!event || typeof event !== "object" || !("date" in event)) continue;
    const label = String(event?.label || "").trim();
    if (!label) continue;

    const lowerLabel = label.toLowerCase();
    const isContract = lowerLabel.includes("contrat");
    const isPlanned = lowerLabel.includes("versement prévu");
    if (!isContract && !isPlanned) continue;

    const date = toValidDate(event.date);
    if (!date) continue;
    const day = toDayStart(date);
    const dayTs = day.getTime();

    if (isPlanned) {
      if (dayTs < today.getTime()) {
        pushItem("overdue", label, day);
        continue;
      }
      if (dayTs === today.getTime()) {
        pushItem("today", label, day);
        continue;
      }
      if (dayTs <= upcomingLimit.getTime()) {
        pushItem("upcoming", label, day);
      }
      continue;
    }

    if (dayTs === today.getTime()) {
      pushItem("today", label, day);
      continue;
    }
    if (dayTs > today.getTime() && dayTs <= upcomingLimit.getTime()) {
      pushItem("upcoming", label, day);
    }
  }

  return items;
}

type ProjectTrackingSnapshot = {
  contractProjectIds: number[];
  verdeoContractProjectIds: number[];
  internalContractProjectIds: number[];
  plannedProjectIds: number[];
  receivedProjectIds: number[];
  terrainProjectIds: number[];
  calendarByProjectId: Record<number, CalendarEventInput[]>;
};

function extractProjectsFromByProjectFundingResponse(res: any): any[] {
  const raw = (res as any)?.data ?? res;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function readIndicatorProjectId(indicator: any): number | null {
  const raw =
    typeof indicator?.project === "number"
      ? indicator.project
      : indicator?.project?.id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function collectIndicatorYears(
  indicators: Array<{ year?: unknown } | null | undefined>,
): number[] {
  const years = new Set<number>();

  for (const indicator of indicators || []) {
    const year = Number(indicator?.year);
    const amount = Number(indicator?.valueDisplay ?? indicator?.value ?? 0);
    if (
      Number.isFinite(year) &&
      year > 0 &&
      Number.isFinite(amount) &&
      amount > 0
    ) {
      years.add(year);
    }
  }

  return Array.from(years).sort((a, b) => a - b);
}

function buildProjectTrackingSnapshot(
  fundingRes: any,
  indicatorsRes: any,
): ProjectTrackingSnapshot {
  const contracts = new Set<number>();
  const verdeoContracts = new Set<number>();
  const internalContracts = new Set<number>();
  const planned = new Set<number>();
  const received = new Set<number>();
  const terrain = new Set<number>();
  const calendarByProjectId: Record<number, CalendarEventInput[]> = {};

  const projects = extractProjectsFromByProjectFundingResponse(fundingRes);
  for (const project of projects) {
    const projectId = Number(project?.id);
    if (!projectId || Number.isNaN(projectId)) continue;
    const projectName = String(project?.name || `Projet #${projectId}`);
    const targetId = `funding-project-card-${projectId}`;

    const fundings = normalizeCollectionLike(project?.realFundings);
    const obtainedFundings = normalizeCollectionLike(project?.obtainedFunding);
    if (fundings.length > 0 || obtainedFundings.length > 0)
      contracts.add(projectId);

    for (const funding of fundings) {
      if (readFundingIsVerdeo(funding)) {
        verdeoContracts.add(projectId);
      } else {
        internalContracts.add(projectId);
      }

      const contractDate = readRealFundingContractDateRaw(funding);
      if (contractDate) {
        calendarByProjectId[projectId] = calendarByProjectId[projectId] || [];
        calendarByProjectId[projectId].push({
          date: contractDate,
          label: readFundingIsVerdeo(funding)
            ? `${projectName} · Contrat via Verdeo`
            : `${projectName} · Contrat interne`,
          targetId,
          projectId,
        });
      }

      const expectedPaids = normalizeCollectionLike(
        funding?.realFundingExpectedPaids,
      );
      if (
        expectedPaids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return Number.isFinite(amount) && amount > 0;
        })
      ) {
        planned.add(projectId);
      }

      if (
        expectedPaids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return (
            Number(paid?.status) === 2 && Number.isFinite(amount) && amount > 0
          );
        })
      ) {
        received.add(projectId);
      }

      for (const paid of expectedPaids) {
        if (paid?.at) {
          calendarByProjectId[projectId] = calendarByProjectId[projectId] || [];
          calendarByProjectId[projectId].push({
            date: paid.at,
            label: `${projectName} · Versement prévu`,
            targetId,
            projectId,
          });
        }
        if (paid?.datePaid) {
          calendarByProjectId[projectId] = calendarByProjectId[projectId] || [];
          calendarByProjectId[projectId].push({
            date: paid.datePaid,
            label: `${projectName} · Versement reçu`,
            targetId,
            projectId,
          });
        }
      }

      const paids = normalizeCollectionLike(funding?.realFundingPaids);
      if (
        paids.some((paid) => {
          const amount = Number(paid?.amount ?? 0);
          return Number.isFinite(amount) && amount > 0;
        })
      ) {
        received.add(projectId);
      }

      for (const paid of paids) {
        if (paid?.at) {
          calendarByProjectId[projectId] = calendarByProjectId[projectId] || [];
          calendarByProjectId[projectId].push({
            date: paid.at,
            label: `${projectName} · Versement reçu`,
            targetId,
            projectId,
          });
        }
      }
    }

    for (const obtainedFunding of obtainedFundings) {
      if (readObtainedFundingIsVerdeo(obtainedFunding)) {
        verdeoContracts.add(projectId);
      } else {
        internalContracts.add(projectId);
      }

      const obtainedDate = readObtainedFundingDate(obtainedFunding);
      if (obtainedDate) {
        calendarByProjectId[projectId] = calendarByProjectId[projectId] || [];
        calendarByProjectId[projectId].push({
          date: obtainedDate,
          label: readObtainedFundingIsVerdeo(obtainedFunding)
            ? `${projectName} · Contrat via Verdeo`
            : `${projectName} · Contrat interne`,
          targetId,
          projectId,
        });
      }
    }
  }

  const indicators = Array.isArray(indicatorsRes)
    ? indicatorsRes
    : Array.isArray((indicatorsRes as any)?.data)
      ? (indicatorsRes as any).data
      : [];

  for (const indicator of indicators) {
    const projectId = readIndicatorProjectId(indicator);
    if (projectId) terrain.add(projectId);
  }

  return {
    contractProjectIds: Array.from(contracts),
    verdeoContractProjectIds: Array.from(verdeoContracts),
    internalContractProjectIds: Array.from(internalContracts),
    plannedProjectIds: Array.from(planned),
    receivedProjectIds: Array.from(received),
    terrainProjectIds: Array.from(terrain),
    calendarByProjectId,
  };
}

function mergeProjectTrackingSnapshots(
  base: ProjectTrackingSnapshot,
  incoming: ProjectTrackingSnapshot,
): ProjectTrackingSnapshot {
  const toUnique = (a: number[], b: number[]) =>
    Array.from(new Set([...(a || []), ...(b || [])]));

  const calendarByProjectId: Record<number, CalendarEventInput[]> = {
    ...(base.calendarByProjectId || {}),
  };
  for (const [projectIdRaw, dates] of Object.entries(
    incoming.calendarByProjectId || {},
  )) {
    const projectId = Number(projectIdRaw);
    if (!projectId || Number.isNaN(projectId)) continue;
    calendarByProjectId[projectId] = [
      ...(calendarByProjectId[projectId] || []),
      ...(dates || []),
    ];
  }

  return {
    contractProjectIds: toUnique(
      base.contractProjectIds,
      incoming.contractProjectIds,
    ),
    verdeoContractProjectIds: toUnique(
      base.verdeoContractProjectIds,
      incoming.verdeoContractProjectIds,
    ),
    internalContractProjectIds: toUnique(
      base.internalContractProjectIds,
      incoming.internalContractProjectIds,
    ),
    plannedProjectIds: toUnique(
      base.plannedProjectIds,
      incoming.plannedProjectIds,
    ),
    receivedProjectIds: toUnique(
      base.receivedProjectIds,
      incoming.receivedProjectIds,
    ),
    terrainProjectIds: toUnique(
      base.terrainProjectIds,
      incoming.terrainProjectIds,
    ),
    calendarByProjectId,
  };
}

/** ===================== Parent ===================== */

export default function NewFundingByProject({
  year,
  idProject,
  forceEuroDisplay = false,
  onAvailableYearsChange,
}: {
  year: number;
  idProject?: number;
  forceEuroDisplay?: boolean;
  onAvailableYearsChange?: (years: number[]) => void;
}) {
  // ✅ NEW: lecture URL (openAddFunding/idProject/idFunder)
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const didAutoOpenCleanRef = useRef(false);

  const urlOpenAddFunding = useMemo(() => {
    const raw = (searchParams.get("openAddFunding") || "").toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes";
  }, [searchParams]);

  const urlIdProject = useMemo(() => {
    const v = Number(searchParams.get("idProject"));
    return v && !Number.isNaN(v) ? v : null;
  }, [searchParams]);
  const urlOpenProjectId = useMemo(() => {
    const v = Number(searchParams.get("openProjectId"));
    return v && !Number.isNaN(v) ? v : null;
  }, [searchParams]);

  const urlIdFunder = useMemo(() => {
    const v = Number(searchParams.get("idFunder"));
    return v && !Number.isNaN(v) ? v : null;
  }, [searchParams]);

  const urlOpenDesiredFunding = useMemo(() => {
    const raw = (searchParams.get("openDesiredFunding") || "").toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes";
  }, [searchParams]);

  // ✅ priorité: prop idProject (route) > query idProject
  const resolvedProjectId = useMemo(() => {
    const p = Number(idProject);
    if (p && !Number.isNaN(p)) return p;
    if (urlIdProject) return urlIdProject;
    return undefined;
  }, [idProject, urlIdProject]);

  const { fetchData } = useFetchDataV2("");

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [initialProjects, setInitialProjects] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectDetailsById, setProjectDetailsById] = useState<
    Record<number, any>
  >({});
  const [projectIndicatorsById, setProjectIndicatorsById] = useState<
    Record<number, any[]>
  >({});
  const [loadingProjectIds, setLoadingProjectIds] = useState<
    Record<number, boolean>
  >({});
  const [loadingIndicatorsByProjectId, setLoadingIndicatorsByProjectId] =
    useState<Record<number, boolean>>({});
  const [funders, setFunders] = useState<Funder[]>([]);
  const [loadingFunders, setLoadingFunders] = useState(false);
  const [trackingSnapshot, setTrackingSnapshot] =
    useState<ProjectTrackingSnapshot>({
      contractProjectIds: [],
      verdeoContractProjectIds: [],
      internalContractProjectIds: [],
      plannedProjectIds: [],
      receivedProjectIds: [],
      terrainProjectIds: [],
      calendarByProjectId: {},
    });
  const availableIndicatorYears = useMemo(() => {
    if (!resolvedProjectId) return [];
    return collectIndicatorYears(projectIndicatorsById[resolvedProjectId] || []);
  }, [projectIndicatorsById, resolvedProjectId]);
  const [loadingTrackingFunding, setLoadingTrackingFunding] = useState(true);
  const [loadingTrackingIndicators, setLoadingTrackingIndicators] =
    useState(true);
  const [globalProjectDetailsById, setGlobalProjectDetailsById] = useState<
    Record<number, any>
  >({});
  const [globalIndicatorsByProjectId, setGlobalIndicatorsByProjectId] =
    useState<Record<number, any[]>>({});
  const [trackingSearch, setTrackingSearch] = useState("");
  const [trackingFilter, setTrackingFilter] =
    useState<ProjectTrackingFilterKey>("all");
  const [calendarFocus, setCalendarFocus] = useState<{
    projectId: number;
    nonce: number;
  } | null>(null);
  const fundersPromiseRef = useRef<Promise<Funder[]> | null>(null);
  const priorityProjectsPrefetchKeyRef = useRef<string>("");
  const projectLoadPromisesRef = useRef<Partial<Record<number, Promise<void>>>>(
    {},
  );

  useEffect(() => {
    onAvailableYearsChange?.(availableIndicatorYears);
  }, [availableIndicatorYears, onAvailableYearsChange]);

  useEffect(() => {
    let canceled = false;
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const programs = await fetchData(
          `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
        );
        if (canceled) return;

        let list = extractProjectsFromPrograms(programs);
        if (
          typeof resolvedProjectId !== "undefined" &&
          resolvedProjectId !== null
        ) {
          list = list.filter(
            (p: any) => Number(p?.id) === Number(resolvedProjectId),
          );
        }

        setInitialProjects(list);
        setProjects(list);
      } finally {
        if (!canceled) setLoadingProjects(false);
      }
    };

    void loadProjects();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedProjectId]);

  useEffect(() => {
    let canceled = false;

    const loadTrackingSnapshot = async () => {
      const singleProjectId =
        Number.isFinite(Number(resolvedProjectId)) &&
        Number(resolvedProjectId) > 0
          ? Number(resolvedProjectId)
          : null;

      setLoadingTrackingFunding(true);
      setLoadingTrackingIndicators(true);
      setTrackingSnapshot({
        contractProjectIds: [],
        verdeoContractProjectIds: [],
        internalContractProjectIds: [],
        plannedProjectIds: [],
        receivedProjectIds: [],
        terrainProjectIds: [],
        calendarByProjectId: {},
      });

      const loadFunding = async () => {
        try {
          const fundingRes = await fetchData(
            singleProjectId
              ? `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project&id=${singleProjectId}`
              : `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project`,
          );
          if (canceled) return;

          const fundingSnapshot = buildProjectTrackingSnapshot(fundingRes, []);
          setTrackingSnapshot((prev) =>
            mergeProjectTrackingSnapshots(prev, fundingSnapshot),
          );

          const extractedProjects =
            extractProjectsFromByProjectFundingResponse(fundingRes);
          const normalizedProjects = formatData(
            extractedProjects.map((project: any) => ({
              ...project,
              realFundings: Array.isArray(project?.realFundings)
                ? project.realFundings
                : [],
              desiredFunding: Array.isArray(project?.desiredFunding)
                ? project.desiredFunding
                : [],
            })),
          );
          const nextGlobalProjectDetailsById: Record<number, any> = {};
          for (const project of normalizedProjects) {
            const projectId = Number(project?.id);
            if (!projectId || Number.isNaN(projectId)) continue;
            nextGlobalProjectDetailsById[projectId] = project;
          }
          setGlobalProjectDetailsById(nextGlobalProjectDetailsById);
          setProjectDetailsById((prev) => ({
            ...prev,
            ...nextGlobalProjectDetailsById,
          }));
        } catch {
          if (canceled) return;
          // conserve les valeurs précédentes si l'API funding échoue
        } finally {
          if (!canceled) setLoadingTrackingFunding(false);
        }
      };

      const loadIndicators = async () => {
        try {
          const indicatorsRes = await fetchData(
            singleProjectId
              ? `${import.meta.env.VITE_API_ENDPOINT}/indicator/data/virement?project=${singleProjectId}`
              : `${import.meta.env.VITE_API_ENDPOINT}/indicator/data/virement`,
          );
          if (canceled) return;

          const indicatorsSnapshot = buildProjectTrackingSnapshot(
            [],
            indicatorsRes,
          );
          setTrackingSnapshot((prev) =>
            mergeProjectTrackingSnapshots(prev, indicatorsSnapshot),
          );

          const nextGlobalIndicatorsByProjectId: Record<number, any[]> = {};
          for (const indicator of extractIndicatorsFromResponse(
            indicatorsRes,
          )) {
            const projectId = readIndicatorProjectId(indicator);
            if (!projectId) continue;
            nextGlobalIndicatorsByProjectId[projectId] =
              nextGlobalIndicatorsByProjectId[projectId] || [];
            nextGlobalIndicatorsByProjectId[projectId].push(indicator);
          }
          setGlobalIndicatorsByProjectId(nextGlobalIndicatorsByProjectId);
          setProjectIndicatorsById((prev) => ({
            ...prev,
            ...nextGlobalIndicatorsByProjectId,
          }));
        } catch {
          if (canceled) return;
          // conserve les valeurs précédentes si l'API indicateurs échoue
        } finally {
          if (!canceled) setLoadingTrackingIndicators(false);
        }
      };

      void loadFunding();
      void loadIndicators();
    };

    void loadTrackingSnapshot();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedProjectId]);

  const ensureFundersLoaded = useCallback(async (): Promise<Funder[]> => {
    if (funders.length > 0) return funders;
    if (fundersPromiseRef.current) return fundersPromiseRef.current;

    setLoadingFunders(true);
    const promise = Promise.all([
      fetchData(`${import.meta.env.VITE_API_ENDPOINT}/financers`).catch(
        () => [],
      ),
      fetchData(`${import.meta.env.VITE_API_ENDPOINT}/my-financers`).catch(
        () => [],
      ),
    ])
      .then(([allRes, myRes]) => {
        const allFunders = Array.isArray(allRes) ? allRes : [];
        const myFunders = Array.isArray(myRes) ? myRes : [];
        const selfFunder = myFunders.find((f) => !!f?.isSelf) ?? null;

        let next = allFunders.map((f) =>
          selfFunder && Number(f?.id) === Number(selfFunder?.id)
            ? { ...f, ...selfFunder, isSelf: true }
            : f,
        );

        if (
          selfFunder &&
          !next.some((f) => Number(f?.id) === Number(selfFunder?.id))
        ) {
          next = [selfFunder, ...next];
        }

        if (selfFunder) {
          const selfId = Number(selfFunder.id);
          next = [
            ...next.filter((f) => Number(f?.id) === selfId),
            ...next.filter((f) => Number(f?.id) !== selfId),
          ];
        }

        setFunders(next);
        return next as Funder[];
      })
      .finally(() => {
        setLoadingFunders(false);
        fundersPromiseRef.current = null;
      });

    fundersPromiseRef.current = promise;
    return promise;
  }, [fetchData, funders]);

  const ensureProjectDataLoaded = useCallback(
    async (
      projectId: number,
      options?: { allowDirectFetch?: boolean; forceIndicatorFetch?: boolean },
    ) => {
      if (!projectId || Number.isNaN(projectId)) return;
      const hasLocalIndicators = Array.isArray(
        projectIndicatorsById[projectId],
      );
      if (
        projectDetailsById[projectId] &&
        hasLocalIndicators &&
        !options?.forceIndicatorFetch
      ) {
        return;
      }
      if (projectLoadPromisesRef.current[projectId]) {
        return projectLoadPromisesRef.current[projectId];
      }

      const promise = (async () => {
        setLoadingProjectIds((prev) => ({ ...prev, [projectId]: true }));
        setLoadingIndicatorsByProjectId((prev) => ({
          ...prev,
          [projectId]: true,
        }));

        try {
          await ensureFundersLoaded();

          let detailProject = globalProjectDetailsById[projectId];
          let projectIndicators = Array.isArray(
            globalIndicatorsByProjectId[projectId],
          )
            ? globalIndicatorsByProjectId[projectId]
            : [];

          const shouldFetchProjectDirect =
            options?.allowDirectFetch === true && !detailProject;
          const shouldFetchIndicatorsDirect =
            options?.forceIndicatorFetch === true ||
            (options?.allowDirectFetch === true &&
              projectIndicators.length === 0);

          if (shouldFetchProjectDirect || shouldFetchIndicatorsDirect) {
            const [projectResResult, indicatorsResResult] =
              await Promise.allSettled([
                shouldFetchProjectDirect
                  ? fetchData(
                      `${import.meta.env.VITE_API_ENDPOINT}/real-fundings?by=project&id=${projectId}`,
                    )
                  : Promise.resolve(null),
                shouldFetchIndicatorsDirect
                  ? fetchData(
                      `${import.meta.env.VITE_API_ENDPOINT}/indicator/data/virement?project=${projectId}`,
                    )
                  : Promise.resolve(null),
              ]);

            if (
              shouldFetchProjectDirect &&
              projectResResult.status === "fulfilled"
            ) {
              const directProject = extractProjectByIdFromFundingResponse(
                projectResResult.value,
                projectId,
              );
              if (directProject?.id) {
                detailProject = {
                  ...directProject,
                  realFundings: Array.isArray(directProject?.realFundings)
                    ? directProject.realFundings
                    : [],
                  desiredFunding: Array.isArray(directProject?.desiredFunding)
                    ? directProject.desiredFunding
                    : [],
                };
              }
            }

            if (
              shouldFetchIndicatorsDirect &&
              indicatorsResResult.status === "fulfilled"
            ) {
              projectIndicators = extractIndicatorsFromResponse(
                indicatorsResResult.value,
              );
            }
          }

          const summaryProject =
            initialProjects.find((p) => Number(p?.id) === Number(projectId)) ??
            projects.find((p) => Number(p?.id) === Number(projectId));
          const mergedProject = {
            ...(summaryProject || {}),
            ...(detailProject || {}),
          };

          if (mergedProject?.id) {
            const normalizedProject = formatData([
              {
                ...mergedProject,
                realFundings: Array.isArray(mergedProject?.realFundings)
                  ? mergedProject.realFundings
                  : [],
                desiredFunding: Array.isArray(mergedProject?.desiredFunding)
                  ? mergedProject.desiredFunding
                  : [],
              },
            ])[0];

            setProjectDetailsById((prev) => ({
              ...prev,
              [projectId]: normalizedProject,
            }));
          }

          setProjectIndicatorsById((prev) => ({
            ...prev,
            [projectId]: projectIndicators,
          }));
        } finally {
          setLoadingProjectIds((prev) => ({ ...prev, [projectId]: false }));
          setLoadingIndicatorsByProjectId((prev) => ({
            ...prev,
            [projectId]: false,
          }));
          delete projectLoadPromisesRef.current[projectId];
        }
      })();

      projectLoadPromisesRef.current[projectId] = promise;
      return promise;
    },
    [
      ensureFundersLoaded,
      fetchData,
      globalIndicatorsByProjectId,
      globalProjectDetailsById,
      initialProjects,
      projectIndicatorsById,
      projectDetailsById,
      projects,
    ],
  );

  useEffect(() => {
    if (loadingProjects) return;

    const priorityIds: number[] = [];
    const directProjectId = Number(resolvedProjectId || 0);
    const warningProjectId = Number(urlOpenProjectId || 0);
    const firstProjectId = Number(projects[0]?.id || initialProjects[0]?.id || 0);

    if (Number.isFinite(directProjectId) && directProjectId > 0) {
      priorityIds.push(directProjectId);
    }
    if (
      Number.isFinite(warningProjectId) &&
      warningProjectId > 0 &&
      !priorityIds.includes(warningProjectId)
    ) {
      priorityIds.push(warningProjectId);
    }
    if (
      Number.isFinite(firstProjectId) &&
      firstProjectId > 0 &&
      !priorityIds.includes(firstProjectId)
    ) {
      priorityIds.push(firstProjectId);
    }

    if (priorityIds.length === 0) return;

    const prefetchKey = priorityIds.join(",");
    if (priorityProjectsPrefetchKeyRef.current === prefetchKey) return;
    priorityProjectsPrefetchKeyRef.current = prefetchKey;

    priorityIds.forEach((projectId) => {
      void ensureProjectDataLoaded(projectId, {
        allowDirectFetch: true,
        forceIndicatorFetch: true,
      });
    });
  }, [
    ensureProjectDataLoaded,
    initialProjects,
    loadingProjects,
    projects,
    resolvedProjectId,
    urlOpenProjectId,
  ]);

  // ✅ NEW: nettoyage URL (évite de ré-ouvrir au re-render/back)
  useEffect(() => {
    if (didAutoOpenCleanRef.current) return;
    if (!urlOpenAddFunding) return;
    if (!resolvedProjectId) return;
    if (loadingProjects) return;

    // uniquement si le projet cible est bien présent dans la liste finale
    const exists = (projects || []).some(
      (p) => Number(p.id) === Number(resolvedProjectId),
    );
    if (!exists) return;

    didAutoOpenCleanRef.current = true;

    const next = new URLSearchParams(searchParams);
    next.delete("openAddFunding");
    next.delete("openDesiredFunding");
    next.delete("idFunder");
    next.delete("idProject"); // safe même si pas présent
    setSearchParams(next, { replace: true });
  }, [
    urlOpenAddFunding,
    resolvedProjectId,
    loadingProjects,
    projects,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (loadingProjects) return;
    if (resolvedProjectId) return;
    if (!urlOpenProjectId) return;

    const targetId = `funding-project-card-${Number(urlOpenProjectId)}`;
    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) return false;
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return true;
    };

    const rafId = requestAnimationFrame(() => {
      scrollToTarget();
    });
    const timeoutA = window.setTimeout(() => {
      scrollToTarget();
    }, 250);
    const timeoutB = window.setTimeout(() => {
      scrollToTarget();
    }, 700);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutA);
      window.clearTimeout(timeoutB);
    };
  }, [loadingProjects, resolvedProjectId, urlOpenProjectId, projects.length]);

  const contractProjectIdsSet = useMemo(
    () => new Set(trackingSnapshot.contractProjectIds),
    [trackingSnapshot.contractProjectIds],
  );
  const verdeoContractProjectIdsSet = useMemo(
    () => new Set(trackingSnapshot.verdeoContractProjectIds),
    [trackingSnapshot.verdeoContractProjectIds],
  );
  const internalContractProjectIdsSet = useMemo(
    () => new Set(trackingSnapshot.internalContractProjectIds),
    [trackingSnapshot.internalContractProjectIds],
  );
  const plannedProjectIdsSet = useMemo(
    () => new Set(trackingSnapshot.plannedProjectIds),
    [trackingSnapshot.plannedProjectIds],
  );
  const receivedProjectIdsSet = useMemo(
    () => new Set(trackingSnapshot.receivedProjectIds),
    [trackingSnapshot.receivedProjectIds],
  );
  const terrainProjectIdsSet = useMemo(
    () => new Set(trackingSnapshot.terrainProjectIds),
    [trackingSnapshot.terrainProjectIds],
  );

  const hasContractsForProject = useCallback(
    (project: any) => {
      const id = Number(project?.id);
      if (id && contractProjectIdsSet.has(id)) return true;
      return hasProjectContracts(project);
    },
    [contractProjectIdsSet],
  );
  const hasVerdeoContractsForProject = useCallback(
    (project: any) => {
      const id = Number(project?.id);
      if (id && verdeoContractProjectIdsSet.has(id)) return true;
      return hasProjectVerdeoContracts(project);
    },
    [verdeoContractProjectIdsSet],
  );
  const hasInternalContractsForProject = useCallback(
    (project: any) => {
      const id = Number(project?.id);
      if (id && internalContractProjectIdsSet.has(id)) return true;
      return hasProjectInternalContracts(project);
    },
    [internalContractProjectIdsSet],
  );
  const hasPlannedForProject = useCallback(
    (project: any) => {
      const id = Number(project?.id);
      if (id && plannedProjectIdsSet.has(id)) return true;
      return hasProjectPlannedPayments(project);
    },
    [plannedProjectIdsSet],
  );
  const hasReceivedForProject = useCallback(
    (project: any) => {
      const id = Number(project?.id);
      if (id && receivedProjectIdsSet.has(id)) return true;
      return hasProjectReceivedPayments(project);
    },
    [receivedProjectIdsSet],
  );
  const hasTerrainForProject = useCallback(
    (project: any) => {
      const id = Number(project?.id);
      if (id && terrainProjectIdsSet.has(id)) return true;
      return hasProjectTerrainPayments(project, projectIndicatorsById);
    },
    [projectIndicatorsById, terrainProjectIdsSet],
  );

  const normalizedTrackingSearch = (trackingSearch || "").trim().toLowerCase();

  const visibleProjects = useMemo(() => {
    return projects.filter((project) => {
      if (normalizedTrackingSearch) {
        const haystack = [
          String(project?.name ?? ""),
          String(project?.program?.name ?? ""),
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(normalizedTrackingSearch)) return false;
      }

      switch (trackingFilter) {
        case "needs":
          return hasProjectNeeds(project);
        case "contracts_verdeo":
          return hasVerdeoContractsForProject(project);
        case "contracts_internal":
          return hasInternalContractsForProject(project);
        case "contracts":
          return hasContractsForProject(project);
        case "planned":
          return hasPlannedForProject(project);
        case "received":
          return hasReceivedForProject(project);
        case "terrain":
          return hasTerrainForProject(project);
        case "all":
        default:
          return true;
      }
    });
  }, [
    hasContractsForProject,
    hasInternalContractsForProject,
    hasPlannedForProject,
    hasReceivedForProject,
    hasTerrainForProject,
    hasVerdeoContractsForProject,
    normalizedTrackingSearch,
    projects,
    trackingFilter,
  ]);

  const visibleProjectsForTotals = useMemo(() => {
    return visibleProjects.map((project) => {
      const projectId = Number(project?.id);
      if (!projectId || Number.isNaN(projectId)) return project;
      const detailedProject = projectDetailsById[projectId];
      return detailedProject ? { ...project, ...detailedProject } : project;
    });
  }, [projectDetailsById, visibleProjects]);

  const userStructureLabel = useMemo(() => {
    const value = String(user?.structureName || "").trim();
    return value || "votre structure";
  }, [user?.structureName]);

  const totalContractsAmount = useMemo(
    () =>
      round2(
        visibleProjectsForTotals.reduce(
          (sum, project) => sum + sumProjectContractAmountEur(project),
          0,
        ),
      ),
    [visibleProjectsForTotals],
  );

  const totalExpectedPaymentsAmount = useMemo(
    () =>
      round2(
        visibleProjectsForTotals.reduce(
          (sum, project) => sum + sumProjectExpectedPaymentsEur(project),
          0,
        ),
      ),
    [visibleProjectsForTotals],
  );

  const totalReceivedPaymentsAmount = useMemo(
    () =>
      round2(
        visibleProjectsForTotals.reduce(
          (sum, project) => sum + sumProjectReceivedPaymentsEur(project),
          0,
        ),
      ),
    [visibleProjectsForTotals],
  );

  const totalTerrainPaymentsAmount = useMemo(
    () =>
      round2(
        visibleProjectsForTotals.reduce((sum, project) => {
          const projectId = Number(project?.id);
          if (!projectId || Number.isNaN(projectId)) return sum;
          return (
            sum + sumProjectTerrainPaymentsEur(projectIndicatorsById[projectId] || [])
          );
        }, 0),
      ),
    [projectIndicatorsById, visibleProjectsForTotals],
  );

  const structurePartAmount = useMemo(
    () => round2(totalContractsAmount - totalTerrainPaymentsAmount),
    [totalContractsAmount, totalTerrainPaymentsAmount],
  );

  const [desiredTotalAmount, setDesiredTotalAmount] = useState(0);
  const [desiredTotalLoading, setDesiredTotalLoading] = useState(false);

  useEffect(() => {
    let canceled = false;

    const computeDesiredTotal = async () => {
      setDesiredTotalLoading(true);
      setDesiredTotalAmount(0);

      let total = 0;
      for (const project of visibleProjectsForTotals) {
        const desiredRows = normalizeCollectionLike(project?.desiredFunding);
        if (desiredRows.length === 0) continue;

        for (const desiredRow of desiredRows) {
          total += await desiredAmountToEUR(
            desiredRow,
            project?.country?.currency,
          );
        }

        if (!canceled) {
          setDesiredTotalAmount(round2(total));
        }
      }

      if (!canceled) {
        setDesiredTotalAmount(round2(total));
        setDesiredTotalLoading(false);
      }
    };

    void computeDesiredTotal();
    return () => {
      canceled = true;
    };
  }, [visibleProjectsForTotals]);

  const trackingStats = useMemo<IntroStat[]>(
    () => [
      {
        key: "desired",
        label: "Financements souhaités",
        value: desiredTotalAmount,
        valueDisplay: formatEuroStatAmount(desiredTotalAmount),
        meta: "Total en euros",
        tone: "sand",
      },
      {
        key: "contracts_total",
        label: "Total des contrats",
        value: totalContractsAmount,
        valueDisplay: formatEuroStatAmount(totalContractsAmount),
        meta: "Tous contrats confondus",
        tone: "blue",
      },
      {
        key: "planned_total",
        label: "Versements prévus",
        value: totalExpectedPaymentsAmount,
        valueDisplay: formatEuroStatAmount(totalExpectedPaymentsAmount),
        meta: "Total prévu",
        tone: "teal",
      },
      {
        key: "received_total",
        label: "Versements reçus",
        value: totalReceivedPaymentsAmount,
        valueDisplay: formatEuroStatAmount(totalReceivedPaymentsAmount),
        meta: "Total reçu",
        tone: "violet",
      },
      {
        key: "terrain_total",
        label: "Versements terrain",
        value: totalTerrainPaymentsAmount,
        valueDisplay: formatEuroStatAmount(totalTerrainPaymentsAmount),
        meta: "Réalisés",
        tone: "olive",
      },
      {
        key: "structure_part",
        label: `Part ${userStructureLabel}`,
        value: structurePartAmount,
        valueDisplay: formatEuroStatAmount(structurePartAmount),
        meta: "Contrats - versements terrain",
        tone: "rose",
        hideDots: true,
      },
    ],
    [
      desiredTotalAmount,
      structurePartAmount,
      totalContractsAmount,
      totalExpectedPaymentsAmount,
      totalReceivedPaymentsAmount,
      totalTerrainPaymentsAmount,
      userStructureLabel,
    ],
  );

  const introLoadingStatKeys = useMemo(() => {
    const keys = new Set<string>();
    if (desiredTotalLoading) {
      keys.add("desired");
    }
    const hasAnyFundingLoaded = visibleProjectsForTotals.some(
      (project) =>
        readProjectFundings(project).length > 0 ||
        readProjectObtainedFundings(project).length > 0,
    );
    if (loadingTrackingFunding && !hasAnyFundingLoaded) {
      keys.add("contracts_total");
      keys.add("planned_total");
      keys.add("received_total");
      keys.add("structure_part");
    }
    if (loadingTrackingIndicators) {
      keys.add("terrain_total");
      keys.add("structure_part");
    }
    return Array.from(keys);
  }, [
    desiredTotalLoading,
    loadingTrackingFunding,
    loadingTrackingIndicators,
    projectIndicatorsById,
    visibleProjectsForTotals,
  ]);

  const introCalendarEvents = useMemo(() => {
    const events: CalendarEventInput[] = [];
    for (const project of visibleProjects) {
      const projectId = Number(project?.id);
      const snapshotEvents =
        projectId && trackingSnapshot.calendarByProjectId[projectId]
          ? trackingSnapshot.calendarByProjectId[projectId]
          : [];
      if (snapshotEvents.length > 0) {
        events.push(...snapshotEvents);
        continue;
      }
      events.push(...collectProjectCalendarEvents(project));
    }
    return events;
  }, [trackingSnapshot.calendarByProjectId, visibleProjects]);

  const introReminderItems = useMemo(() => {
    const reminders: FundingReminderItem[] = [];
    for (const project of visibleProjects) {
      const projectId = Number(project?.id);
      const detailed =
        projectId && projectDetailsById[projectId]
          ? projectDetailsById[projectId]
          : null;
      if (detailed) {
        reminders.push(
          ...collectProjectReminderItems({ ...project, ...detailed }),
        );
        continue;
      }

      const snapshotEvents =
        projectId && trackingSnapshot.calendarByProjectId[projectId]
          ? trackingSnapshot.calendarByProjectId[projectId]
          : [];
      if (snapshotEvents.length > 0) {
        reminders.push(
          ...collectProjectReminderItemsFromCalendarEvents(
            project,
            snapshotEvents,
          ),
        );
        continue;
      }

      reminders.push(...collectProjectReminderItems(project));
    }
    return reminders;
  }, [
    projectDetailsById,
    trackingSnapshot.calendarByProjectId,
    visibleProjects,
  ]);

  const handleResetIntro = useCallback(() => {
    setTrackingSearch("");
    setTrackingFilter("all");
  }, []);

  const handleCalendarEventClick = useCallback((item: CalendarDetailItem) => {
    const projectId = Number(item?.projectId);
    if (!projectId || Number.isNaN(projectId)) return;

    setCalendarFocus({ projectId, nonce: Date.now() });

    const targetId = item?.targetId || `funding-project-card-${projectId}`;
    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.requestAnimationFrame(scrollToTarget);
    window.setTimeout(scrollToTarget, 180);
    window.setTimeout(scrollToTarget, 520);
  }, []);

  const handleReminderClick = useCallback(
    (item: FundingReminderItem) => {
      handleCalendarEventClick({
        label: item.label,
        count: 1,
        targetId: item.targetId,
        projectId: item.projectId,
        funderId: item.funderId,
      });
    },
    [handleCalendarEventClick],
  );

  if (loadingProjects) return <FundingSkeleton />;

  return (
    <div className="space-y-6" data-tour-funding-project-root>
      <FundingTrackingTutorial mode="project" />

      <FundingTrackingIntroPanel
        title="Suivi de financement"
        badge={`${visibleProjects.length} projet(s)`}
        subtitle="Suivez les financements souhaités, les contrats et les versements sur les projets affichés."
        searchPlaceholder="Rechercher un projet (nom, programme)…"
        searchValue={trackingSearch}
        onSearchChange={setTrackingSearch}
        onReset={handleResetIntro}
        resultsCount={visibleProjects.length}
        stats={trackingStats}
        calendarEvents={introCalendarEvents}
        onCalendarEventClick={handleCalendarEventClick}
        reminderItems={introReminderItems}
        onReminderClick={handleReminderClick}
        loadingStatKeys={introLoadingStatKeys}
      />

      {visibleProjects.map((project, index) => {
        const pid = Number(project.id);
        const shouldAutoOpen =
          urlOpenAddFunding &&
          !!resolvedProjectId &&
          pid === Number(resolvedProjectId);
        const shouldOpenFromWarning =
          !resolvedProjectId &&
          !!urlOpenProjectId &&
          pid === Number(urlOpenProjectId);
        const allowIndependentLoad =
          index === 0 || shouldAutoOpen || shouldOpenFromWarning;
        const detailedProject = projectDetailsById[pid] || project;

        return (
          <div
            key={project.id}
            data-tour-funding-project-card={index === 0 ? "true" : undefined}
          >
            <ItemProject
              project={detailedProject}
              funders={funders}
              setFunders={setFunders}
              projectIndicators={projectIndicatorsById[pid] || []}
              loadingIndicators={
                !!loadingIndicatorsByProjectId[pid] ||
                (loadingTrackingIndicators &&
                  !Array.isArray(projectIndicatorsById[pid]))
              }
              isProjectLoaded={!!projectDetailsById[pid]}
              isProjectLoading={
                !!loadingProjectIds[pid] ||
                !!loadingIndicatorsByProjectId[pid] ||
                loadingFunders
              }
              onEnsureProjectDataLoaded={() =>
                ensureProjectDataLoaded(pid, {
                  allowDirectFetch: true,
                  forceIndicatorFetch: true,
                })
              }
              year={year}
              noTitle={
                typeof resolvedProjectId !== "undefined" &&
                resolvedProjectId !== null
              }
              // ✅ NEW: auto-open wizard + preselect funder from URL
              autoOpenAddFunding={!!shouldAutoOpen}
              autoOpenDesiredFunding={!!urlOpenDesiredFunding}
              prefillFunderId={urlIdFunder ?? undefined}
              defaultOpen={
                shouldOpenFromWarning ||
                (index === 0 &&
                  !(
                    typeof resolvedProjectId !== "undefined" &&
                    resolvedProjectId !== null
                  ) &&
                  !urlOpenProjectId)
              }
              scrollOnMountOpen={shouldOpenFromWarning}
              cardDomId={`funding-project-card-${pid}`}
              forceEuroDisplay={forceEuroDisplay}
              calendarFocusKey={
                calendarFocus?.projectId === pid ? calendarFocus.nonce : 0
              }
            />
          </div>
        );
      })}

      {visibleProjects.length === 0 ? (
        <div className="rounded-xl border bg-white p-4 text-sm text-muted-foreground">
          Aucun projet ne correspond aux filtres sélectionnés.
        </div>
      ) : null}
    </div>
  );
}

/** ===================== Types ===================== */

type Funder = {
  id: number;
  fullname: string;
  structureName?: string;
  name?: string;
  isSelf?: boolean;
  representants: Array<{
    id: number;
    firstname: string;
    lastname: string;
    email?: string;
    phone?: string;
  }>;
};

type MediaLight = { id: number; name?: string; uri?: string };
type ExpectedPaidDraft = { id: string; amount: number; date: string };

type SavedMeta = {
  desiredFundingIds?: number[];
  desiredFundingAllocations?: Array<{ id: number; amountEUR: number }>;
  desiredProjectRateToEuro?: number | null;
};

/** ===================== ItemProject ===================== */

const ItemProject = ({
  project,
  funders,
  setFunders,
  projectIndicators,
  loadingIndicators,
  isProjectLoaded,
  isProjectLoading,
  onEnsureProjectDataLoaded,
  year,
  noTitle = false,
  autoOpenAddFunding = false,
  autoOpenDesiredFunding = false,
  prefillFunderId,
  defaultOpen = false,
  scrollOnMountOpen = false,
  cardDomId,
  forceEuroDisplay = false,
  calendarFocusKey = 0,
}: {
  project: any;
  funders: Funder[];
  setFunders: React.Dispatch<React.SetStateAction<Funder[]>>;
  projectIndicators: any[];
  loadingIndicators: boolean;
  isProjectLoaded: boolean;
  isProjectLoading: boolean;
  onEnsureProjectDataLoaded: () => Promise<void>;
  year: number;
  noTitle?: boolean;

  // ✅ NEW
  autoOpenAddFunding?: boolean;
  autoOpenDesiredFunding?: boolean;
  prefillFunderId?: number;
  defaultOpen?: boolean;
  scrollOnMountOpen?: boolean;
  cardDomId?: string;
  forceEuroDisplay?: boolean;
  calendarFocusKey?: number;
}) => {
  const [isOpen, setIsOpen] = useState(
    () => noTitle || autoOpenAddFunding || defaultOpen,
  );
  const [currencySelected, setCurrencySelected] = useState("EUR");

  const [convertedFundings, setConvertedFundings] = useState<any[]>(
    project.realFundings || [],
  );
  const [convertedIndicators, setConvertedIndicators] = useState<any[]>([]);
  const [loadingFundingCards, setLoadingFundingCards] = useState(false);

  // ✅ NEW: desired timeline corrigée (conversion devise -> EUR avant somme)
  const [desiredByMonth, setDesiredByMonth] = useState<number[]>(
    Array(12).fill(0),
  );

  const projectCurrency = project?.country?.currency;
  const projectCurrencyCode = projectCurrency?.code;
  const projectCurrencySymbol = projectCurrency?.symbol || "";

  const [isAdding, setIsAdding] = useState(false);

  const [isWizardOpen, setWizardOpen] = useState(false);
  const [isDesiredWizardOpen, setDesiredWizardOpen] = useState(false);

  const didAutoOpenRef = useRef(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const didAutoScrollRef = useRef(false);

  const filterProjectIndicators = () =>
    (projectIndicators || []).filter((pi) => pi.project?.id === project.id);

  const rawProjectFundings = Array.isArray(project?.realFundings)
    ? project.realFundings
    : [];
  const rawProjectIndicators = filterProjectIndicators();
  const detailFundings =
    convertedFundings.length > 0 || rawProjectFundings.length === 0
      ? convertedFundings
      : rawProjectFundings;
  const detailIndicators =
    convertedIndicators.length > 0 || rawProjectIndicators.length === 0
      ? convertedIndicators
      : rawProjectIndicators;

  const readPaidAmountConverted = (payment: any): number | null => {
    const converted = Number(
      payment?.amountConverted ??
        payment?.amount_converted ??
        payment?.paidAmountConverted ??
        payment?.paid_amount_converted,
    );
    return Number.isFinite(converted) && converted > 0 ? converted : null;
  };

  const convertPaymentAmountForDisplay = async (
    amountEurInput: number,
    payment: any,
    fundingRateToEuro: number | null,
    projectCurrencyLike: any,
    preferStoredConvertedAmount: boolean,
  ): Promise<number> => {
    const amountEur = Number(amountEurInput);
    if (!Number.isFinite(amountEur) || amountEur <= 0) return 0;

    if (preferStoredConvertedAmount) {
      const storedConverted = readPaidAmountConverted(payment);
      if (storedConverted) return storedConverted;
    }

    if (
      Number.isFinite(Number(fundingRateToEuro)) &&
      Number(fundingRateToEuro) > 0
    ) {
      return amountEur / Number(fundingRateToEuro);
    }

    return Number(await convertEuroToCurrency(amountEur, projectCurrencyLike));
  };

  const buildConvertedFundings = async (currency: string) => {
    const base = project.realFundings || [];
    return Promise.all(
      base.map(async (funding: any) => {
        const fundingInputCurrencyCode = readFundingInputCurrencyCode(
          funding,
          projectCurrency,
        );
        const fundingInputIsLocalCurrency =
          !!fundingInputCurrencyCode && fundingInputCurrencyCode !== "EUR";
        const convertedStored = readRealFundingConvertedAmount(funding);
        const historicalRateToEuro =
          projectCurrencyCode && projectCurrencyCode !== "EUR"
            ? await fetchHexaRateRateToEuroAtDate(
                projectCurrencyCode,
                readRealFundingContractDateRaw(funding),
              )
            : null;
        const fallbackStoredRateToEuro = readRealFundingRateToEuro(funding);
        const effectiveRateToEuro =
          historicalRateToEuro &&
          Number.isFinite(historicalRateToEuro) &&
          historicalRateToEuro > 0
            ? historicalRateToEuro
            : fallbackStoredRateToEuro &&
                Number.isFinite(fallbackStoredRateToEuro) &&
                fallbackStoredRateToEuro > 0
              ? fallbackStoredRateToEuro
              : null;
        const storedBaseEur = Number(
          funding?.paidAmountEUR ?? funding?.paidAmount ?? 0,
        );
        const baseEur =
          fundingInputIsLocalCurrency && convertedStored && effectiveRateToEuro
            ? round2(convertedStored * effectiveRateToEuro)
            : storedBaseEur;
        if (!baseEur || isNaN(baseEur)) {
          return { ...funding, paidAmount: 0, paidAmountEUR: 0 };
        }

        if (currency === "EUR" || !projectCurrencyCode) {
          return { ...funding, paidAmount: baseEur, paidAmountEUR: baseEur };
        }
        const converted =
          fundingInputIsLocalCurrency && convertedStored
            ? convertedStored
            : effectiveRateToEuro
              ? baseEur / effectiveRateToEuro
              : await convertEuroToCurrency(baseEur, projectCurrency);
        return {
          ...funding,
          paidAmount: Number(converted),
          paidAmountEUR: baseEur,
          realFundingPaidsConverted: await buildPaidsFromFunding(
            funding,
            projectCurrency,
            effectiveRateToEuro,
            fundingInputIsLocalCurrency,
          ),
          realFundingExpectedPaidsConverted:
            await buildExpectedPaidsFromFunding(
              funding,
              projectCurrency,
              effectiveRateToEuro,
              fundingInputIsLocalCurrency,
            ),
        };
      }),
    );
  };

  const buildPaidsFromFunding = async (
    funding: any,
    projectCurrencyLike: any,
    fundingRateToEuro: number | null,
    fundingInputIsLocalCurrency: boolean,
  ) => {
    return await Promise.all(
      (funding.realFundingPaids || []).map(async (p: any) => ({
        ...p,
        amount: await convertPaymentAmountForDisplay(
          Number(p.amount ?? 0),
          p,
          fundingRateToEuro,
          projectCurrencyLike,
          fundingInputIsLocalCurrency,
        ),
      })),
    );
  };

  const buildExpectedPaidsFromFunding = async (
    funding: any,
    projectCurrencyLike: any,
    fundingRateToEuro: number | null,
    fundingInputIsLocalCurrency: boolean,
  ) => {
    if (!Array.isArray(funding?.realFundingExpectedPaids)) return [];
    return await Promise.all(
      funding.realFundingExpectedPaids.map(async (p: any) => ({
        ...p,
        amount: await convertPaymentAmountForDisplay(
          Number(p.amount ?? 0),
          p,
          fundingRateToEuro,
          projectCurrencyLike,
          fundingInputIsLocalCurrency,
        ),
      })),
    );
  };

  const buildConvertedIndicators = async (currency: string) => {
    const list = filterProjectIndicators();
    return Promise.all(
      list.map(async (pi: any) => {
        const baseVal = Number(pi?.value ?? 0) || 0;
        if (currency === "EUR" || !projectCurrencyCode) {
          return { ...pi, valueDisplay: baseVal };
        }
        const converted = await convertEuroToCurrency(baseVal, projectCurrency);
        return { ...pi, valueDisplay: Number(converted) };
      }),
    );
  };

  // ✅ NEW: Desired -> EUR -> display currency
  const buildDesiredByMonth = async (currency: string) => {
    const list = Array.isArray(project?.desiredFunding)
      ? project.desiredFunding
      : [];

    let totalEUR = 0;
    for (const df of list) {
      totalEUR += await desiredAmountToEUR(df, projectCurrency);
    }

    let display = totalEUR;
    if (
      currency !== "EUR" &&
      projectCurrency?.code &&
      projectCurrency.code !== "EUR"
    ) {
      try {
        display = toNumber(
          await convertEuroToCurrency(totalEUR, projectCurrency),
        );
      } catch {
        display = totalEUR;
      }
    }

    const arr = Array(12).fill(0) as number[];
    arr[0] = display || 0; // ✅ comme avant: tout en janvier
    return arr;
  };

  const handleCurrencyChange = async (currency: string) => {
    setCurrencySelected(currency);
    setLoadingFundingCards(true);
    try {
      const [newFundings, newIndicators, newDesired] = await Promise.all([
        buildConvertedFundings(currency),
        buildConvertedIndicators(currency),
        buildDesiredByMonth(currency),
      ]);
      setConvertedFundings(newFundings);
      setConvertedIndicators(newIndicators);
      setDesiredByMonth(newDesired);
    } finally {
      setLoadingFundingCards(false);
    }
  };

  useEffect(() => {
    if (!forceEuroDisplay) return;
    if (currencySelected === "EUR") return;
    void handleCurrencyChange("EUR");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceEuroDisplay, currencySelected]);

  const extractRealFundings = (res: any): any[] => {
    const asFundingArray = (candidate: any): any[] => {
      if (Array.isArray(candidate)) return candidate;
      if (!candidate || typeof candidate !== "object") return [];
      const values = Object.values(candidate);
      if (!values.length) return [];
      const seemsFundingCollection = values.every(
        (item) =>
          item &&
          typeof item === "object" &&
          ("id" in item ||
            "paidAmount" in item ||
            "financier" in item ||
            "financer" in item),
      );
      return seemsFundingCollection ? (values as any[]) : [];
    };

    const direct = asFundingArray(res);
    if (direct.length > 0) return direct;

    const fromData = asFundingArray(res?.data);
    if (fromData.length > 0) return fromData;

    const fromRealFundings = asFundingArray(res?.realFundings);
    if (fromRealFundings.length > 0) return fromRealFundings;

    const fromFundings = asFundingArray(res?.fundings);
    if (fromFundings.length > 0) return fromFundings;

    if (res && typeof res === "object" && res.id) return [res];
    return [];
  };

  // ✅ IMPORTANT: on enlève les besoins couverts de project.desiredFunding (donc total timeline ↓)
  const applyDesiredRemoval = async (meta?: SavedMeta) => {
    if (!Array.isArray(project?.desiredFunding)) return;

    const allocations = meta?.desiredFundingAllocations || [];
    if (Array.isArray(allocations) && allocations.length > 0) {
      const allocById = new Map<number, number>(
        allocations
          .map((a): [number, number] => [Number(a?.id), Number(a?.amountEUR)])
          .filter(([id, amt]) => !!id && !isNaN(id) && !!amt && !isNaN(amt)),
      );

      const nextDesired: any[] = [];
      for (const df of project.desiredFunding) {
        const id = Number(df?.id);
        const allocated = allocById.get(id);
        if (!id || !allocated) {
          nextDesired.push(df);
          continue;
        }

        const desiredEur = await desiredAmountToEUR(df, projectCurrency, {
          projectRateToEuro: meta?.desiredProjectRateToEuro,
        });
        if (!desiredEur || isNaN(desiredEur)) {
          nextDesired.push(df);
          continue;
        }

        if (allocated >= desiredEur - 0.0001) {
          // entièrement couvert -> on retire
          continue;
        }

        const ratio = Math.min(1, Math.max(0, allocated / desiredEur));
        const baseAmount = toNumber(df?.amount ?? df?.value ?? df?.montant);
        const remainingAmount = round2(baseAmount * (1 - ratio));
        const remainingEur = round2(desiredEur - allocated);

        df.amount = remainingAmount;
        if ("value" in df) df.value = remainingAmount;
        if ("montant" in df) df.montant = remainingAmount;
        df.convertedAmount = remainingEur;

        nextDesired.push(df);
      }

      project.desiredFunding = nextDesired;
      return;
    }

    const ids = meta?.desiredFundingIds || [];
    if (!Array.isArray(ids) || ids.length === 0) return;

    const setIds = new Set(ids.map((n) => Number(n)));
    project.desiredFunding = project.desiredFunding.filter(
      (df: any) => !setIds.has(Number(df?.id)),
    );
  };

  const handleSaved = async (apiResult: any, meta?: SavedMeta) => {
    // 1) met à jour les contrats (retour API)
    const next = extractRealFundings(apiResult);
    if (next.length) {
      project.realFundings = next; // base en EUR
    }

    // 2) retire de la timeline "souhaités" ceux qui viennent d'être couverts par ce contrat
    await applyDesiredRemoval(meta);

    // 3) refresh affichage (fundings/indicators/desired)
    await handleCurrencyChange(currencySelected);

    setIsAdding(false);
    setWizardOpen(false);
    setDesiredWizardOpen(false);
  };

  const handleUpdated = async (apiResult: any) => {
    const next = extractRealFundings(apiResult);
    if (next.length) {
      project.realFundings = next;
      await handleCurrencyChange(currencySelected);
    }
  };

  const handleDeleted = async (apiResult: any) => {
    const next = extractRealFundings(apiResult);
    if (next.length) {
      project.realFundings = next;
      await handleCurrencyChange(currencySelected);
      return;
    }
    project.realFundings = (project.realFundings || []).filter(
      (f: any) => Number(f.id) !== Number(apiResult?.id),
    );
    await handleCurrencyChange(currencySelected);
  };

  useEffect(() => {
    if (!isProjectLoaded) return;
    let cancelled = false;
    (async () => {
      setLoadingFundingCards(true);
      try {
        const [newFundings, newIndicators, newDesired] = await Promise.all([
          buildConvertedFundings(currencySelected),
          buildConvertedIndicators(currencySelected),
          buildDesiredByMonth(currencySelected),
        ]);
        if (cancelled) return;
        setConvertedFundings(newFundings);
        setConvertedIndicators(newIndicators);
        setDesiredByMonth(newDesired);
      } finally {
        if (!cancelled) {
          setLoadingFundingCards(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isProjectLoaded,
    projectIndicators,
    project?.id,
    project?.desiredFunding,
    project?.realFundings,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (isProjectLoaded || isProjectLoading) return;
    void onEnsureProjectDataLoaded();
  }, [isOpen, isProjectLoaded, isProjectLoading, onEnsureProjectDataLoaded]);

  // ✅ NEW: auto-open wizard sur URL openAddFunding (+ prefill funder)
  useEffect(() => {
    if (!autoOpenAddFunding) return;
    if (didAutoOpenRef.current) return;
    didAutoOpenRef.current = true;

    setIsOpen(true);
    void onEnsureProjectDataLoaded().then(() => {
      if (autoOpenDesiredFunding) {
        setDesiredWizardOpen(true);
      } else {
        setWizardOpen(true);
      }
    });
  }, [autoOpenAddFunding, autoOpenDesiredFunding, onEnsureProjectDataLoaded]);

  useEffect(() => {
    if (!scrollOnMountOpen || !isOpen) return;
    if (didAutoScrollRef.current) return;
    didAutoScrollRef.current = true;

    requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [isOpen, scrollOnMountOpen]);

  useEffect(() => {
    if (!calendarFocusKey) return;
    setIsOpen(true);
    requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [calendarFocusKey]);

  const onToggleProject = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next && !isProjectLoading) {
      void onEnsureProjectDataLoaded();
    }
  };

  const onClickAddFunding = () => {
    setDesiredWizardOpen(true);
  };

  const desiredCount = Array.isArray(project?.desiredFunding)
    ? project.desiredFunding.length
    : 0;

  return (
    <div id={cardDomId} ref={cardRef} className="scroll-mt-20">
      <Card key={project.id} className="border-none bg-[#F7F9FB]">
        <CardHeader
          className="flex flex-row items-center justify-between cursor-pointer"
          data-tour-funding-project-card-header="true"
        >
          <h2 className="font-semibold text-[#434653] flex items-center gap-2">
            {!noTitle && (
              <a
                href={`/dashboard/project/${project.id}`}
                className="text-[#434653] text-[0.875rem] hover:underline flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={24} className="w-4 h-4 text-[#434653]" />
                {project.name}
              </a>
            )}
          </h2>

          <div className="flex items-center gap-2">
            {!forceEuroDisplay ? (
              <Select
                value={currencySelected}
                onValueChange={handleCurrencyChange}
                disabled={!isProjectLoaded}
              >
                <SelectTrigger className="w-[110px]">
                  <SelectValue placeholder="Devise" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">€</SelectItem>
                  {projectCurrencyCode && projectCurrencyCode !== "EUR" && (
                    <SelectItem value={projectCurrencyCode}>
                      {projectCurrencySymbol || projectCurrencyCode}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            ) : (
              <span className="inline-flex h-9 min-w-[64px] items-center justify-center rounded-md border border-input bg-background px-3 text-sm">
                €
              </span>
            )}

            <button className="p-1" onClick={onToggleProject}>
              {isOpen ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </button>
          </div>
        </CardHeader>

        {isOpen && (
          <CardContent>
            {!isProjectLoaded ? (
              <div className="py-2">
                <FundingSkeleton />
              </div>
            ) : (
              <>
                <div data-tour-funding-project-matrix="true">
                  <FundingMonthlyMatrix
                    year={year}
                    convertedFundings={convertedFundings}
                    projectIndicatorsConverted={convertedIndicators}
                    fundingsLoading={loadingFundingCards}
                    indicatorsLoading={loadingIndicators}
                    project={project}
                    currencySelected={currencySelected}
                    desiredByMonth={desiredByMonth} // ✅ corrected totals
                    onCreateContractFromDesired={() =>
                      setDesiredWizardOpen(true)
                    }
                    desiredCtaDisabled={desiredCount === 0}
                    desiredCount={desiredCount}
                  />
                </div>

                <div className="mt-8 flex items-center gap-3">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                    Vue détaillée (contrats & versements)
                  </div>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>

                {detailFundings?.length > 0 || isAdding ? (
                  <div className="mt-3" data-tour-funding-project-detail="true">
                    {detailFundings.map((funding, idx) => (
                      <div className="mb-3" key={funding.id ?? idx}>
                        <FundingRow
                          funding={funding}
                          project={project}
                          projectIndicators={detailIndicators}
                          loadingProjectIndicators={loadingIndicators}
                          currencySelected={currencySelected}
                          projectCurrencySymbol={projectCurrencySymbol}
                          funders={funders}
                          setFunders={setFunders}
                          onUpdated={handleUpdated}
                          onDeleted={handleDeleted}
                        />
                      </div>
                    ))}
                    <Table>
                      <TableBody>
                        {isAdding && (
                          <AddFundingRow
                            project={project}
                            currencySelected={currencySelected}
                            funders={funders}
                            setFunders={setFunders}
                            onSaved={handleSaved}
                            onCancel={() => setIsAdding(false)}
                          />
                        )}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p
                    className="text-gray-500 text-sm mt-3"
                    data-tour-funding-project-detail="true"
                  >
                    Aucun financement obtenu (contrat signé) enregistré.
                  </p>
                )}

                <div className="mt-3 flex justify-center">
                  <Button
                    id="add_funding"
                    variant="outline"
                    onClick={onClickAddFunding}
                    disabled={isAdding}
                  >
                    Créer un financement obtenu (contrat)
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        )}

        {isProjectLoaded && (
          <>
            <NewFundingWizardModal
              mode="default"
              open={isWizardOpen}
              onOpenChange={setWizardOpen}
              project={project}
              funders={funders}
              setFunders={setFunders}
              currencySelected={currencySelected}
              onSaved={handleSaved}
              // ✅ NEW: pré-sélection financeur si idFunder en URL
              prefillFunderId={prefillFunderId}
            />

            <NewFundingWizardModal
              mode="desiredFirst"
              open={isDesiredWizardOpen}
              onOpenChange={setDesiredWizardOpen}
              project={project}
              funders={funders}
              setFunders={setFunders}
              currencySelected={currencySelected}
              onSaved={handleSaved}
              prefillFunderId={prefillFunderId}
            />
          </>
        )}
      </Card>
    </div>
  );
};

/** ===================== Wizard Modal ===================== */

type WizardMode = "default" | "desiredFirst";

function NewFundingWizardModal({
  mode = "default",
  open,
  onOpenChange,
  project,
  funders,
  setFunders,
  currencySelected,
  onSaved,
  prefillFunderId,
}: {
  mode?: WizardMode;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  project: any;
  funders: Funder[];
  setFunders: React.Dispatch<React.SetStateAction<Funder[]>>;
  currencySelected: string;
  onSaved: (apiResult: any, meta?: SavedMeta) => Promise<void> | void;

  // ✅ NEW
  prefillFunderId?: number;
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const [openAddFunderModal, setOpenAddFunderModal] = useState(false);
  const [openAddRepresentantModal, setOpenAddRepresentantModal] =
    useState(false);
  const [funderId, setFunderId] = useState<string>("");
  const [representantId, setRepresentantId] = useState<string>("");
  const [paidAmount, setPaidAmount] = useState<string>("");
  const [paidAmountTouched, setPaidAmountTouched] = useState(false);
  const [autoFillContractAmountError, setAutoFillContractAmountError] =
    useState<string | null>(null);
  const [isAutoFillingContractAmount, setIsAutoFillingContractAmount] =
    useState(false);
  const [dateAmount, setDateAmount] = useState<string>("");
  const [contractFile, setContractFile] = useState<MediaLight | null>(null);
  const [skipContractAttachment, setSkipContractAttachment] = useState(false);
  const [forceAllocationStep, setForceAllocationStep] = useState(false);

  const didPrefillRef = useRef(false);

  // ✅ NEW: pré-sélection financeur (une fois à l'ouverture)
  useEffect(() => {
    if (!open) {
      didPrefillRef.current = false;
      return;
    }
    if (!prefillFunderId) return;
    if (didPrefillRef.current) return;

    // ne pas écraser si l'utilisateur a déjà choisi
    if (!funderId) {
      setFunderId(String(prefillFunderId));
      setRepresentantId("");
      didPrefillRef.current = true;
    }
  }, [open, prefillFunderId, funderId]);

  const [selectedDesiredFundingIds, setSelectedDesiredFundingIds] = useState<
    number[]
  >([]);
  const [quickAddDesiredId, setQuickAddDesiredId] = useState<string>("");

  const desiredFundings: any[] = Array.isArray(project?.desiredFunding)
    ? project.desiredFunding
    : [];

  const projectCurrency = project?.country?.currency;
  const projectCurrencyId: number | undefined = projectCurrency?.id;
  const projectCurrencySymbol =
    projectCurrency?.symbol || projectCurrency?.code || "";
  const [currentRateInfo, setCurrentRateInfo] = useState<Awaited<
    ReturnType<typeof resolveCurrentRateInfoWithTodayApi>
  > | null>(null);
  useEffect(() => {
    let canceled = false;
    setCurrentRateInfo(null);

    (async () => {
      const resolved =
        await resolveCurrentRateInfoWithTodayApi(projectCurrency);
      if (!canceled) setCurrentRateInfo(resolved);
    })();

    return () => {
      canceled = true;
    };
  }, [
    projectCurrency?.id,
    projectCurrency?.code,
    projectCurrency?.symbol,
    projectCurrency?.exchangeRates?.[0]?.ratedAt,
  ]);
  const currentRateDateLabel = useMemo(
    () => formatRateDateLabel(currentRateInfo?.ratedAt),
    [currentRateInfo?.ratedAt],
  );
  const [contractDateRateInfo, setContractDateRateInfo] = useState<{
    rateToEuro: number;
    dateLabel: string | null;
    isFallback: boolean;
    warningMessage?: string | null;
  } | null>(null);

  useEffect(() => {
    let canceled = false;

    (async () => {
      const code = String(projectCurrency?.code ?? "")
        .trim()
        .toUpperCase();

      if (!dateAmount || !code || code === "EUR") {
        if (!canceled) setContractDateRateInfo(null);
        return;
      }

      const resolved = await fetchHexaRateRateToEuroAtDateDetailed(
        code,
        dateAmount,
      );

      if (
        (resolved.status === "ok" || resolved.status === "eur") &&
        typeof resolved.rateToEuro === "number" &&
        resolved.rateToEuro > 0
      ) {
        if (!canceled) {
          setContractDateRateInfo({
            rateToEuro: resolved.rateToEuro,
            dateLabel: formatRateDateLabel(resolved.dateUsed ?? dateAmount),
            isFallback: false,
            warningMessage: null,
          });
        }
        return;
      }

      const fallbackRate = Number(currentRateInfo?.rateToEuro ?? 0);
      if (!canceled) {
        setContractDateRateInfo(
          Number.isFinite(fallbackRate) && fallbackRate > 0
            ? {
                rateToEuro: fallbackRate,
                dateLabel: currentRateDateLabel,
                isFallback: true,
                warningMessage:
                  resolved.status === "http_404"
                    ? "Impossible de récupérer le taux à la date du contrat. Le taux actuel est utilisé pour l'affichage."
                    : null,
              }
            : null,
        );
      }
    })();

    return () => {
      canceled = true;
    };
  }, [
    dateAmount,
    projectCurrency?.code,
    currentRateInfo?.rateToEuro,
    currentRateDateLabel,
  ]);

  const resolveDesiredComparisonProjectRateToEuro = useCallback(async () => {
    const code = String(projectCurrency?.code ?? "")
      .trim()
      .toUpperCase();
    if (!code) return null;
    if (code === "EUR") return 1;

    if (dateAmount) {
      const historicalRate = await fetchHexaRateRateToEuroAtDate(
        code,
        dateAmount,
      );
      if (typeof historicalRate === "number" && historicalRate > 0) {
        return historicalRate;
      }
    }

    const fallbackRate = Number(currentRateInfo?.rateToEuro ?? 0);
    return Number.isFinite(fallbackRate) && fallbackRate > 0
      ? fallbackRate
      : null;
  }, [projectCurrency?.code, dateAmount, currentRateInfo?.rateToEuro]);

  // ✅ NEW: total souhaités sélectionnés en EUR (correct)
  const [chosenDesiredTotalEUR, setChosenDesiredTotalEUR] = useState(0);

  useEffect(() => {
    let canceled = false;
    (async () => {
      if (!desiredFundings?.length || !selectedDesiredFundingIds.length) {
        if (!canceled) setChosenDesiredTotalEUR(0);
        return;
      }
      const projectRateToEuro =
        await resolveDesiredComparisonProjectRateToEuro();
      const setIds = new Set(selectedDesiredFundingIds.map((n) => Number(n)));
      let sum = 0;
      for (const df of desiredFundings) {
        const id = Number(df?.id);
        if (!id || !setIds.has(id)) continue;
        sum += await desiredAmountToEUR(df, projectCurrency, {
          projectRateToEuro,
        });
      }
      if (!canceled) setChosenDesiredTotalEUR(sum);
    })();
    return () => {
      canceled = true;
    };
  }, [
    desiredFundings,
    selectedDesiredFundingIds,
    projectCurrency?.id,
    projectCurrency?.code,
    dateAmount,
    currentRateInfo?.rateToEuro,
    resolveDesiredComparisonProjectRateToEuro,
  ]);

  const paidAmountNumber = useMemo(() => {
    return parseInputNumber(paidAmount);
  }, [paidAmount]);

  const chosenRatio = useMemo(() => {
    if (!chosenDesiredTotalEUR) return 0;
    return Math.min(
      100,
      Math.round((paidAmountNumber / chosenDesiredTotalEUR) * 100),
    );
  }, [chosenDesiredTotalEUR, paidAmountNumber]);

  const desiredVsContractDelta = useMemo(
    () => round2(chosenDesiredTotalEUR - paidAmountNumber),
    [chosenDesiredTotalEUR, paidAmountNumber],
  );

  const contractUncoveredDelta = useMemo(
    () => round2(paidAmountNumber - chosenDesiredTotalEUR),
    [paidAmountNumber, chosenDesiredTotalEUR],
  );

  const needAllocationStep = useMemo(() => {
    if (mode !== "desiredFirst") return false;
    if (!selectedDesiredFundingIds.length) return false;
    if (!paidAmountNumber) return false;
    if (!chosenDesiredTotalEUR) return false;
    return desiredVsContractDelta >= MONEY_COMPARISON_EPSILON;
  }, [
    mode,
    selectedDesiredFundingIds.length,
    paidAmountNumber,
    chosenDesiredTotalEUR,
    desiredVsContractDelta,
  ]);
  const hasInsufficientDesiredCoverage = useMemo(() => {
    if (mode !== "desiredFirst") return false;
    if (!paidAmountNumber) return false;
    return contractUncoveredDelta >= MONEY_COMPARISON_EPSILON;
  }, [mode, paidAmountNumber, contractUncoveredDelta]);
  const remainingDesiredCoverage = useMemo(() => {
    if (!hasInsufficientDesiredCoverage) return 0;
    return contractUncoveredDelta;
  }, [hasInsufficientDesiredCoverage, contractUncoveredDelta]);

  const stepOrder = useMemo(() => {
    if (mode === "desiredFirst") {
      return (
        forceAllocationStep && needAllocationStep ? [1, 2, 4, 3] : [1, 2, 3]
      ) as const;
    }
    return [1, 2, 3] as const;
  }, [mode, needAllocationStep, forceAllocationStep]);

  const step = stepOrder[stepIndex];
  const totalSteps = stepOrder.length;

  useEffect(() => {
    if (step === 4 && !forceAllocationStep) {
      setForceAllocationStep(true);
    }
  }, [step, forceAllocationStep]);

  useEffect(() => {
    if (stepIndex > totalSteps - 1) {
      setStepIndex(totalSteps - 1);
    }
  }, [stepIndex, totalSteps]);

  const selectedDesiredFundings = useMemo(() => {
    const setIds = new Set(selectedDesiredFundingIds.map((n) => Number(n)));
    return desiredFundings.filter((df) => setIds.has(Number(df?.id ?? 0)));
  }, [desiredFundings, selectedDesiredFundingIds]);

  const unselectedDesiredFundings = useMemo(() => {
    const setIds = new Set(selectedDesiredFundingIds.map((n) => Number(n)));
    return desiredFundings.filter((df) => !setIds.has(Number(df?.id ?? 0)));
  }, [desiredFundings, selectedDesiredFundingIds]);

  const [desiredEurById, setDesiredEurById] = useState<Record<number, number>>(
    {},
  );

  useEffect(() => {
    let canceled = false;
    (async () => {
      const projectRateToEuro =
        await resolveDesiredComparisonProjectRateToEuro();
      const map: Record<number, number> = {};
      for (const df of selectedDesiredFundings) {
        const id = Number(df?.id);
        if (!id) continue;
        map[id] = await desiredAmountToEUR(df, projectCurrency, {
          projectRateToEuro,
        });
      }
      if (!canceled) setDesiredEurById(map);
    })();
    return () => {
      canceled = true;
    };
  }, [
    selectedDesiredFundings,
    projectCurrency?.id,
    projectCurrency?.code,
    dateAmount,
    currentRateInfo?.rateToEuro,
    resolveDesiredComparisonProjectRateToEuro,
  ]);

  const [desiredAllocations, setDesiredAllocations] = useState<
    Record<number, string>
  >({});
  const allocationSeedRef = useRef<string>("");

  const buildCappedAllocations = (
    ids: number[],
    total: number,
    maxById: Record<number, number>,
  ) => {
    const result: Record<number, string> = {};
    if (!ids.length) return result;

    const totalRounded = round2(total);
    let remainingTotal = totalRounded;

    const items = ids.map((id) => {
      const raw = maxById[id];
      const cap =
        typeof raw === "number" && !isNaN(raw) && raw > 0
          ? round2(raw)
          : Number.POSITIVE_INFINITY;
      return { id, cap };
    });

    let remaining = [...items];
    while (remaining.length > 0 && remainingTotal > 0) {
      const n = remaining.length;
      const equalShare = round2(remainingTotal / n);

      let cappedCount = 0;
      const nextRemaining: Array<{ id: number; cap: number }> = [];

      for (const item of remaining) {
        if (equalShare > item.cap) {
          const value = round2(item.cap);
          result[item.id] = value.toFixed(2);
          remainingTotal = round2(remainingTotal - value);
          cappedCount += 1;
        } else {
          nextRemaining.push(item);
        }
      }

      if (cappedCount === 0) {
        for (let i = 0; i < nextRemaining.length; i += 1) {
          const item = nextRemaining[i];
          const isLast = i === nextRemaining.length - 1;
          let value = isLast ? round2(remainingTotal) : equalShare;
          if (value > item.cap) value = round2(item.cap);
          result[item.id] = value.toFixed(2);
          remainingTotal = round2(remainingTotal - value);
        }
        break;
      }

      remaining = nextRemaining;
    }

    for (const id of ids) {
      if (!(id in result)) result[id] = "0.00";
    }

    return result;
  };

  useEffect(() => {
    if (!needAllocationStep) return;
    const ids = selectedDesiredFundingIds
      .map((n) => Number(n))
      .filter((n) => !!n && !isNaN(n))
      .sort((a, b) => a - b);
    const capsKey = ids
      .map((id) =>
        desiredEurById[id] === undefined ? "x" : round2(desiredEurById[id]),
      )
      .join(",");
    const seed = `${ids.join(",")}|${round2(paidAmountNumber)}|${capsKey}`;
    if (allocationSeedRef.current === seed) return;

    setDesiredAllocations(
      buildCappedAllocations(ids, paidAmountNumber, desiredEurById),
    );
    allocationSeedRef.current = seed;
  }, [
    needAllocationStep,
    selectedDesiredFundingIds,
    paidAmountNumber,
    desiredEurById,
  ]);

  const allocationTotal = useMemo(() => {
    if (!needAllocationStep) return 0;
    return selectedDesiredFundingIds.reduce((sum, id) => {
      const raw = desiredAllocations[Number(id)];
      return sum + parseInputNumber(raw);
    }, 0);
  }, [needAllocationStep, selectedDesiredFundingIds, desiredAllocations]);

  const allocationExceeds =
    needAllocationStep &&
    round2(allocationTotal - paidAmountNumber) >= MONEY_COMPARISON_EPSILON;

  const allocationRemaining = useMemo(() => {
    if (!needAllocationStep) return 0;
    return round2(paidAmountNumber - allocationTotal);
  }, [needAllocationStep, paidAmountNumber, allocationTotal]);

  const selectedFunder =
    funders.find((f) => String(f.id) === String(funderId)) ?? null;
  const isSelfFunderSelected = !!selectedFunder?.isSelf;
  const selectedRepresentants = isSelfFunderSelected
    ? []
    : (selectedFunder?.representants ?? []);

  useEffect(() => {
    if (!isSelfFunderSelected) return;
    if (representantId) {
      setRepresentantId("");
    }
    if (openAddRepresentantModal) {
      setOpenAddRepresentantModal(false);
    }
  }, [isSelfFunderSelected, representantId, openAddRepresentantModal]);

  const [pjOpen, setPjOpen] = useState(false);
  const [pjType, setPjType] = useState<any>(16);
  const [pjComment, setPjComment] = useState("");
  const [pjFiles, setPjFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedPjs, setUploadedPjs] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();
  const { postData } = usePostData<any>();

  const [expectedPaids, setExpectedPaids] = useState<ExpectedPaidDraft[]>([
    { id: crypto.randomUUID(), amount: 0, date: "" },
  ]);

  const [contractAmountLocal, setContractAmountLocal] = useState<number | null>(
    null,
  );
  useEffect(() => {
    let canceled = false;
    const compute = async () => {
      if (!paidAmountNumber || !projectCurrency) {
        if (!canceled) setContractAmountLocal(null);
        return;
      }
      if (projectCurrency?.code === "EUR") {
        if (!canceled) setContractAmountLocal(null);
        return;
      }
      try {
        const val = await convertEuroToCurrency(
          paidAmountNumber,
          projectCurrency,
        );
        if (!canceled) setContractAmountLocal(Number(val));
      } catch {
        if (!canceled) setContractAmountLocal(null);
      }
    };
    compute();
    return () => {
      canceled = true;
    };
  }, [paidAmountNumber, projectCurrency?.code]);

  const canGoNextFromContract =
    funderId !== "" &&
    !!paidAmountNumber &&
    !!dateAmount &&
    (skipContractAttachment || !!contractFile?.id);

  const removeSelectedPjFile = (index: number) => {
    if (isUploading) return;
    setPjFiles((files) => files.filter((_, i) => i !== index));
  };

  const handleUploadPj = async () => {
    if (pjFiles.length === 0 || !pjType) {
      toast({ title: "Veuillez choisir un type et au moins un fichier." });
      return;
    }
    try {
      setIsUploading(true);
      const data = new FormData();
      data.append("type", "11");
      data.append("subType", pjType);
      data.append("description", pjComment || "Contrat / PJ financement");

      for (const file of pjFiles) {
        let compressed: Blob | null = null;
        try {
          const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
          if (isImage(ext)) {
            compressed = await handleCompressImage(file, 1024, 768, 0.9);
          }
        } catch {
          compressed = null;
        }
        const toAppend = compressed
          ? new File([compressed], file.name, { type: compressed.type })
          : file;
        data.append("files[]", toAppend);
      }

      const newMedias: any = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });

      if (!Array.isArray(newMedias) && newMedias?.success === false) {
        toast({ title: newMedias.message ?? "Upload échoué", duration: 1500 });
        return;
      }

      const metas = (Array.isArray(newMedias) ? newMedias : []).map((m) => ({
        id: m.id,
        name: m.name ?? "Fichier",
      }));

      const existingIds = new Set<number>([
        ...(contractFile?.id ? [contractFile.id] : []),
        ...uploadedPjs.map((p) => p.id),
      ]);

      let rest: Array<{ id: number; name: string }> = [];
      if (!contractFile && metas.length > 0) {
        const [first, ...others] = metas;
        if (!existingIds.has(first.id)) {
          setContractFile({ id: first.id, name: first.name });
          existingIds.add(first.id);
        }
        rest = others;
      } else {
        rest = metas;
      }

      const deduped = rest.filter((m) => !existingIds.has(m.id));
      if (deduped.length > 0) setUploadedPjs((prev) => [...prev, ...deduped]);

      setPjFiles([]);
      setPjType(16);
      setPjComment("");
      setPjOpen(false);
      toast({ title: "Pièce(s) jointe(s) ajoutée(s).", duration: 1200 });
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedPj = (id: number) => {
    if (isUploading) return;
    setUploadedPjs((prev) => prev.filter((p) => p.id !== id));
  };

  const addLine = () =>
    setExpectedPaids((prev) => [
      ...prev,
      { id: crypto.randomUUID(), amount: 0, date: "" },
    ]);

  const removeLine = (id: string) =>
    setExpectedPaids((prev) => prev.filter((p) => p.id !== id));

  const updateLine = (id: string, patch: Partial<ExpectedPaidDraft>) =>
    setExpectedPaids((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );

  const resetAll = () => {
    setStepIndex(0);
    setForceAllocationStep(false);
    setOpenAddFunderModal(false);
    setOpenAddRepresentantModal(false);
    setFunderId("");
    setRepresentantId("");
    setPaidAmount("");
    setPaidAmountTouched(false);
    setAutoFillContractAmountError(null);
    setIsAutoFillingContractAmount(false);
    setDateAmount("");
    setContractFile(null);
    setSkipContractAttachment(false);
    setSelectedDesiredFundingIds([]);
    setQuickAddDesiredId("");
    setDesiredAllocations({});
    allocationSeedRef.current = "";
    setPjFiles([]);
    setPjType(16);
    setPjComment("");
    setUploadedPjs([]);
    setExpectedPaids([{ id: crypto.randomUUID(), amount: 0, date: "" }]);
  };

  const toggleDesired = (id: number) => {
    setSelectedDesiredFundingIds((prev) => {
      const setIds = new Set(prev.map((n) => Number(n)));
      if (setIds.has(Number(id))) setIds.delete(Number(id));
      else setIds.add(Number(id));
      return Array.from(setIds);
    });
  };

  const maybePrefillContractAmountFromDesired = () => {
    if (paidAmountTouched) return;
    if (!chosenDesiredTotalEUR || isNaN(chosenDesiredTotalEUR)) return;
    if (!paidAmount || Number(paidAmount) === 0) {
      const rounded = Math.round(chosenDesiredTotalEUR * 100) / 100;
      setPaidAmount(String(rounded));
    }
  };

  const completeContractAmountFromDesired = useCallback(async () => {
    setAutoFillContractAmountError(null);

    if (!dateAmount) {
      setAutoFillContractAmountError(
        "Veuillez d'abord compléter la date de signature pour récupérer le taux de change.",
      );
      return;
    }

    if (!desiredFundings.length) {
      setAutoFillContractAmountError(
        "Aucun financement souhaité disponible pour calculer le montant.",
      );
      return;
    }

    setIsAutoFillingContractAmount(true);
    try {
      const currencyCode = String(projectCurrency?.code ?? "")
        .trim()
        .toUpperCase();
      let projectRateToEuro: number | null = null;

      if (currencyCode === "EUR") {
        projectRateToEuro = 1;
      } else if (currencyCode) {
        const resolvedRate = await resolveHistoricalRateToEuroAtDate(
          currencyCode,
          dateAmount,
          "contract_date",
        );
        if (
          (resolvedRate.source === "contract_date" ||
            resolvedRate.source === "eur") &&
          typeof resolvedRate.rateToEuro === "number" &&
          resolvedRate.rateToEuro > 0
        ) {
          projectRateToEuro = resolvedRate.rateToEuro;
        }
      }

      if (!projectRateToEuro || projectRateToEuro <= 0) {
        setAutoFillContractAmountError(
          "Impossible de récupérer le taux de change à la date du contrat.",
        );
        return;
      }

      let totalEur = 0;
      for (const df of desiredFundings) {
        totalEur += await desiredAmountToEUR(df, projectCurrency, {
          projectRateToEuro,
        });
      }

      const roundedTotal = round2(totalEur);
      setPaidAmount(roundedTotal.toFixed(2));
      setPaidAmountTouched(true);
    } catch {
      setAutoFillContractAmountError(
        "Impossible de calculer le total des financements souhaités.",
      );
    } finally {
      setIsAutoFillingContractAmount(false);
    }
  }, [dateAmount, desiredFundings, projectCurrency]);

  const goNext = () => {
    if (step === 2 && hasInsufficientDesiredCoverage) {
      toast({
        title: "Affectation incomplète",
        description: `Il reste ${remainingDesiredCoverage.toLocaleString(
          "fr-FR",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )} € à affecter. Pour garantir la traçabilité, 100% des montants reçus doivent être rattachés à un ou plusieurs besoins couverts.`,
        variant: "destructive" as any,
      });
      return;
    }

    const nextIndex = Math.min(stepIndex + 1, totalSteps - 1);
    const nextStep = stepOrder[nextIndex];

    if (nextStep === 1 && mode === "desiredFirst") {
      maybePrefillContractAmountFromDesired();
    }
    setStepIndex(nextIndex);
  };

  const goPrev = () => setStepIndex((i) => Math.max(0, i - 1));

  const goToManualAllocationStep = () => {
    if (!needAllocationStep) return;
    setForceAllocationStep(true);
    if (step === 2) {
      setStepIndex((i) => i + 1);
    }
  };

  const save = async () => {
    try {
      setSaving(true);

      if (!project?.id || !funderId)
        throw new Error("Projet ou financeur manquant.");
      if (!paidAmountNumber) throw new Error("Montant du contrat invalide.");
      if (!dateAmount) throw new Error("Date de signature invalide.");
      if (!skipContractAttachment && !contractFile?.id)
        throw new Error(
          "Veuillez ajouter au moins une pièce jointe (contrat).",
        );
      if (!projectCurrencyId) throw new Error("Devise du projet introuvable.");

      const projectCurrencyCode = String(projectCurrency?.code ?? "")
        .trim()
        .toUpperCase();
      let contractRateToEuro: number | null = null;
      if (projectCurrencyCode && projectCurrencyCode !== "EUR") {
        const cachedRate = Number(contractDateRateInfo?.rateToEuro ?? 0);
        if (
          contractDateRateInfo &&
          !contractDateRateInfo.isFallback &&
          Number.isFinite(cachedRate) &&
          cachedRate > 0
        ) {
          contractRateToEuro = cachedRate;
        } else {
          const resolvedRate = await resolveHistoricalRateToEuroAtDate(
            projectCurrencyCode,
            dateAmount,
            "contract_date",
          );
          if (
            (resolvedRate.source === "contract_date" ||
              resolvedRate.source === "eur") &&
            typeof resolvedRate.rateToEuro === "number" &&
            resolvedRate.rateToEuro > 0
          ) {
            contractRateToEuro = resolvedRate.rateToEuro;
          }
        }
        if (!contractRateToEuro) {
          throw new Error(
            "Impossible de récupérer le taux de change HexaRate à la date du contrat.",
          );
        }
      }

      const paidAmountEuro = paidAmountNumber;

      const expectedPaidsPayload = expectedPaids
        .filter((p) => Number(p.amount) > 0 && p.date)
        .map((p) => ({
          amount: Number(p.amount),
          at: new Date(p.date),
        }));

      const pjsIds = Array.from(
        new Set<number>([
          ...(contractFile?.id ? [contractFile.id] : []),
          ...uploadedPjs.map((p) => p.id),
        ]),
      );

      const payload: any = {
        project: project.id,
        financier: Number(funderId),
        representant: isSelfFunderSelected
          ? null
          : representantId && Number(representantId) > 0
            ? Number(representantId)
            : undefined,

        estimatedAmount: "0",
        estimatedDate: new Date(),
        estimatedDevise: 3,

        paidAmount: paidAmountEuro,
        paidDevise: projectCurrencyId,
        dateAmount: new Date(dateAmount),
        ...(contractRateToEuro
          ? { contractRateToEuro: Number(contractRateToEuro) }
          : {}),

        pjs: pjsIds,
        expectedPaids: expectedPaidsPayload,
      };

      let desiredAllocationsPayload: Array<{ id: number; amount: number }> = [];
      if (needAllocationStep && selectedDesiredFundingIds.length > 0) {
        desiredAllocationsPayload = selectedDesiredFundingIds
          .map((n) => {
            const id = Number(n);
            const amount = parseInputNumber(desiredAllocations[id]);
            return {
              id,
              amount: round2(amount),
            };
          })
          .filter((item) => !!item.id && item.amount > 0);
      }

      if (selectedDesiredFundingIds.length > 0) {
        payload.desiredFundings = needAllocationStep
          ? desiredAllocationsPayload
          : selectedDesiredFundingIds.map((n) => Number(n));
      }

      const res = await postData({
        url: `${
          import.meta.env.VITE_IP_API
        }/api/real-funding/save?by=project&onlyFunding=true`,
        data: payload,
      });

      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        (res as any).success === false
      ) {
        throw new Error((res as any)?.message || "Échec de la sauvegarde.");
      }

      // ✅ On remonte les IDs au parent pour qu'il retire ces "souhaités" du timeline
      const desiredProjectRateToEuro =
        selectedDesiredFundingIds.length > 0
          ? await resolveDesiredComparisonProjectRateToEuro()
          : null;

      const meta: SavedMeta | undefined =
        selectedDesiredFundingIds.length > 0
          ? needAllocationStep
            ? {
                desiredFundingAllocations: desiredAllocationsPayload.map(
                  (a) => ({
                    id: Number(a.id),
                    amountEUR: Number(a.amount),
                  }),
                ),
                desiredProjectRateToEuro,
              }
            : {
                desiredFundingIds: selectedDesiredFundingIds.map((n) =>
                  Number(n),
                ),
                desiredProjectRateToEuro,
              }
          : undefined;

      await onSaved(res, meta);

      resetAll();
      toast({
        title: "Financement obtenu créé",
        description:
          "Le contrat signé, l'association aux besoins (si faite) et l'échéancier (si saisi) ont été enregistrés.",
      });
    } catch (e: any) {
      toast({
        title: "Erreur",
        description: e?.message || "Impossible d'enregistrer",
        variant: "destructive" as any,
      });
    } finally {
      setSaving(false);
    }
  };

  const onChangeFunder = (val: string) => {
    setFunderId(val);
    setRepresentantId("");
  };

  const title =
    mode === "desiredFirst"
      ? "Passer en financement obtenu"
      : "Créer un financement obtenu (contrat signé)";

  const description =
    mode === "desiredFirst"
      ? "Vous avez trouvé un financeur pour un ou plusieurs besoins. Renseignez d’abord le contrat signé, puis associez les financements souhaités concernés. Vous pouvez ensuite ajouter un échéancier de versements (facultatif)."
      : "Renseignez le contrat signé pour transformer une opportunité trouvée en financement obtenu. Vous pouvez associer des financements souhaités (facultatif), puis définir un échéancier de versements (facultatif).";

  const stepLabel = (label: string) => (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          Étape {stepIndex + 1}/{totalSteps}
        </span>
        <span className="text-sm font-semibold text-slate-900">{label}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {mode === "desiredFirst" ? (
          <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" />
            Parcours “Souhaité → Obtenu”
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <FileSignature className="w-3.5 h-3.5" />
            Création de contrat
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) resetAll();
      }}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className="text-muted-foreground text-sm">{description}</p>
        </DialogHeader>

        {(contractDateRateInfo || currentRateInfo) && (
          <div className="rounded-md border bg-slate-50 p-3 text-xs text-slate-700">
            {(() => {
              const rateToEuro =
                contractDateRateInfo?.rateToEuro ?? currentRateInfo?.rateToEuro;
              const currencySymbol =
                currentRateInfo?.currencySymbol || projectCurrencySymbol || "";
              const dateLabel =
                contractDateRateInfo?.dateLabel ?? currentRateDateLabel;
              const isFallback = !!contractDateRateInfo?.isFallback;

              if (!rateToEuro || !currencySymbol) return null;

              return (
                <>
                  <div className="font-medium text-slate-900 inline-flex items-center gap-1.5">
                    {contractDateRateInfo?.warningMessage ? (
                      <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
                    ) : dateAmount && !isFallback ? (
                      <Lock className="w-3.5 h-3.5 text-emerald-600" />
                    ) : null}
                    <span>
                      {dateAmount && !isFallback
                        ? "Taux à la date du contrat"
                        : dateAmount && isFallback
                          ? "Taux actuel utilisé car le taux à la date du contrat est introuvable"
                          : "Taux de change actuel utilisé"}
                    </span>
                  </div>
                  <div className="mt-1">
                    1 {currencySymbol} ={" "}
                    {rateToEuro.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    })}{" "}
                    €
                  </div>
                  <div className="mt-0.5">
                    1 € ={" "}
                    {(1 / rateToEuro).toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    })}{" "}
                    {currencySymbol}
                    {` • en date du ${dateLabel || "date non disponible"}`}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ===================== STEP: Souhaités (facultatif) ===================== */}
        {step === 2 && (
          <div className="space-y-4">
            {stepLabel("Besoins couverts (financements souhaités)")}

            <div className="rounded-xl border bg-white/60 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold">
                    Associer aux financements souhaités (facultatif)
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sélectionnez les besoins que ce contrat couvre. Cela aide à
                    suivre la transition <strong>Souhaité → Obtenu</strong>.
                    Vous pouvez aussi passer cette étape.
                  </p>
                </div>

                <div className="text-right">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Total sélectionné (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {chosenDesiredTotalEUR.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>
              </div>

              {desiredFundings.length > 0 ? (
                <>
                  <div className="mt-4 grid gap-3 md:grid-cols-4">
                    <div className="rounded-lg border bg-white p-3">
                      <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                        Sélection
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {selectedDesiredFundingIds.length} élément(s)
                      </div>
                    </div>

                    <div className="rounded-lg border bg-white p-3">
                      <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                        Total sélectionné (EUR)
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {chosenDesiredTotalEUR.toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        €
                      </div>
                    </div>

                    <div className="rounded-lg border bg-white p-3">
                      <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                        Montant du contrat (EUR)
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {paidAmountNumber.toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        €
                      </div>
                    </div>

                    <div className="rounded-lg border bg-white p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                          Couverture
                        </div>
                        <div className="text-xs font-medium text-slate-700">
                          {paidAmountNumber ? `${chosenRatio}%` : "-"}
                        </div>
                      </div>
                      <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-slate-700"
                          style={{
                            width: `${paidAmountNumber ? chosenRatio : 0}%`,
                          }}
                        />
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        Basé sur EUR (cohérent avec le contrat)
                      </div>
                    </div>
                  </div>

                  {needAllocationStep && (
                    <div className="mt-4 rounded-md border border-yellow-300 bg-yellow-50 p-3 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                      <div className="text-sm text-yellow-700">
                        Le total des financements souhaités sélectionnés dépasse
                        le montant du contrat. Les montants seront alloués
                        automatiquement au prorata du montant du contrat lors de
                        l&apos;enregistrement. Si vous souhaitez ajuster les
                        montants manuellement,{" "}
                        <button
                          type="button"
                          className="underline font-semibold hover:no-underline"
                          onClick={goToManualAllocationStep}
                        >
                          cliquez ici
                        </button>
                        .
                      </div>
                    </div>
                  )}
                  {hasInsufficientDesiredCoverage && (
                    <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <div className="text-sm text-red-700">
                        Pour assurer la traçabilité financière, chaque euro reçu
                        doit être rattaché à un ou plusieurs besoins couverts,
                        afin de savoir précisément où les fonds sont allés. Il
                        reste encore{" "}
                        <strong>
                          {remainingDesiredCoverage.toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          €
                        </strong>{" "}
                        non répartis.
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs text-muted-foreground">
                      Cliquez sur une carte pour sélectionner / désélectionner.
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          setSelectedDesiredFundingIds(
                            desiredFundings
                              .map((d) => Number(d?.id))
                              .filter((n) => !!n && !isNaN(n)),
                          )
                        }
                      >
                        Tout sélectionner
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSelectedDesiredFundingIds([])}
                      >
                        Réinitialiser
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {desiredFundings.map((df: any) => {
                      const id = Number(df?.id);
                      const selected = selectedDesiredFundingIds
                        .map((n) => Number(n))
                        .includes(id);

                      const title =
                        df?.financier?.fullname ||
                        df?.financer?.fullname ||
                        df?.title ||
                        df?.name ||
                        `Besoin #${id || "?"}`;

                      const amount = toNumber(df?.amount ?? 0);
                      const cur = readCurrencyLike(df, projectCurrency);

                      return (
                        <button
                          key={id || title}
                          type="button"
                          onClick={() => id && toggleDesired(id)}
                          className={[
                            "text-left rounded-xl border p-3 transition shadow-sm",
                            "bg-white hover:bg-slate-50",
                            selected
                              ? "border-slate-900 ring-1 ring-slate-900"
                              : "border-slate-200",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 break-words">
                                {title}
                              </div>
                              {df?.comment ? (
                                <div className="text-xs text-muted-foreground truncate mt-0.5">
                                  {df.comment}
                                </div>
                              ) : null}
                            </div>
                            {selected ? (
                              <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[0.65rem] px-2 py-0.5">
                                <CheckCircle2 className="w-3 h-3" />
                                Sélectionné
                              </span>
                            ) : (
                              <span className="shrink-0 rounded-full bg-slate-100 text-slate-700 text-[0.65rem] px-2 py-0.5">
                                Choisir
                              </span>
                            )}
                          </div>

                          <div className="mt-3 flex items-end justify-between">
                            <div className="text-xs text-muted-foreground">
                              Montant (devise d’origine)
                            </div>
                            <div className="text-sm font-semibold text-slate-900">
                              {formatMoney(amount, cur)}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="mt-4 rounded-lg border bg-white p-3 text-sm text-muted-foreground">
                  Aucun financement souhaité trouvé pour ce projet. Vous pouvez
                  passer cette étape.
                </div>
              )}
            </div>

            <DialogFooter className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="secondary" onClick={goPrev}>
                  Précédent
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Fermer
                </Button>
              </div>

              <Button
                onClick={goNext}
                disabled={hasInsufficientDesiredCoverage}
                title={
                  hasInsufficientDesiredCoverage
                    ? "Le montant du contrat n'est pas encore totalement affecté"
                    : step === 2 &&
                        mode === "desiredFirst" &&
                        needAllocationStep &&
                        !forceAllocationStep
                      ? "Continuer avec une répartition automatique au prorata"
                      : "Continuer"
                }
              >
                {step === 2 &&
                mode === "desiredFirst" &&
                needAllocationStep &&
                !forceAllocationStep
                  ? "Continuer (répartition auto)"
                  : "Continuer"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ===================== STEP: Contrat ===================== */}
        {step === 1 && (
          <div className="space-y-6">
            {stepLabel("Contrat signé (financement obtenu)")}

            <div className="space-y-2">
              <Label>
                Financeur<span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Select value={funderId} onValueChange={onChangeFunder}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionner le financeur du contrat" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {funders.map((f) => (
                      <SelectItem key={f.id} value={String(f.id)}>
                        {f.isSelf
                          ? "Moi-même"
                          : f.fullname || f.structureName || f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  className="px-2 py-1 border rounded text-xs whitespace-nowrap"
                  onClick={() => setOpenAddFunderModal(true)}
                  title="Ajouter un financeur"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Si vous financez vous-même le projet, ou si vous ne souhaitez
                pas préciser le financeur, choisissez &quot;Moi-même&quot;.
              </p>
            </div>

            {selectedFunder && !isSelfFunderSelected && (
              <div className="space-y-2">
                <Label>Représentant (optionnel)</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={representantId}
                    onValueChange={setRepresentantId}
                    disabled={!funderId || selectedRepresentants.length === 0}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={
                          !funderId
                            ? "Choisir le financeur d'abord"
                            : selectedRepresentants.length === 0
                              ? "Aucun représentant"
                              : "Sélectionner un représentant"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {selectedRepresentants.length > 0 &&
                        selectedRepresentants.map((r) => (
                          <SelectItem key={r.id} value={String(r.id)}>
                            {r.firstname} {r.lastname}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    className="px-2 py-1 border rounded text-xs whitespace-nowrap disabled:opacity-60"
                    onClick={() => setOpenAddRepresentantModal(true)}
                    disabled={!funderId || saving || isUploading}
                    title={
                      funderId
                        ? "Ajouter un représentant"
                        : "Choisir un financeur d'abord"
                    }
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-xl border p-4 bg-white/50 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <Label>
                      Montant du contrat (€)
                      <span className="text-red-500">*</span>
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[11px]"
                      onClick={completeContractAmountFromDesired}
                      disabled={isAutoFillingContractAmount || saving}
                      title="Compléter à partir du total des financements souhaités"
                    >
                      {isAutoFillingContractAmount ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : null}
                      Compléter
                    </Button>
                  </div>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={paidAmount}
                    onChange={(e) => {
                      setAutoFillContractAmountError(null);
                      setPaidAmountTouched(true);
                      setPaidAmount(e.target.value);
                    }}
                    placeholder="0,00"
                  />
                  {autoFillContractAmountError ? (
                    <div className="mt-1 text-xs text-red-600">
                      {autoFillContractAmountError}
                    </div>
                  ) : null}
                  {desiredFundings.length > 0 ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Astuce : utilisez <strong>Compléter</strong> pour
                      reprendre le total des financements souhaités en EUR (à la
                      date du contrat).
                    </div>
                  ) : null}
                </div>

                <div>
                  <Label>
                    Date de signature
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={dateAmount}
                    onChange={(e) => {
                      setAutoFillContractAmountError(null);
                      setDateAmount(e.target.value);
                    }}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>
                    Contrat signé (PJ){" "}
                    {!skipContractAttachment && (
                      <span className="text-red-500">*</span>
                    )}
                  </Label>
                  <div className="mt-2 flex items-center gap-2">
                    <Checkbox
                      id="skip-contract-attachment"
                      checked={skipContractAttachment}
                      onCheckedChange={(checked) =>
                        setSkipContractAttachment(checked === true)
                      }
                    />
                    <Label
                      htmlFor="skip-contract-attachment"
                      className="text-xs text-muted-foreground cursor-pointer"
                    >
                      Je ne veux pas mettre le contrat
                    </Label>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPjOpen(true)}
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      {skipContractAttachment
                        ? "Ajouter une PJ (optionnel)"
                        : "Ajouter une PJ"}
                    </Button>

                    {contractFile?.id && (
                      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 border rounded px-2 py-1">
                        <span className="max-w-[180px] truncate">
                          {contractFile.name || `media_${contractFile.id}`}
                        </span>
                        <button
                          type="button"
                          className="p-0.5 hover:opacity-80"
                          onClick={() => setContractFile(null)}
                          aria-label="Retirer"
                          title="Retirer"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>

                  {uploadedPjs.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {uploadedPjs.map((p) => (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 text-xs bg-gray-100 border rounded px-2 py-1"
                          title={p.name}
                        >
                          <span className="max-w-[160px] truncate">
                            {p.name}
                          </span>
                          <button
                            type="button"
                            className="p-0.5 hover:opacity-80"
                            onClick={() => removeUploadedPj(p.id)}
                            aria-label={`Retirer ${p.name}`}
                            title="Retirer"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Montant saisi en euros (valeur de référence dans Verdeo).
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={goPrev}>
                    Précédent
                  </Button>
                  <Button
                    onClick={goNext}
                    disabled={!canGoNextFromContract}
                    title={
                      canGoNextFromContract
                        ? "Continuer"
                        : "Complétez le financeur, le montant, la date et une PJ"
                    }
                  >
                    Continuer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================== STEP: Répartition ===================== */}
        {step === 4 && (
          <div className="space-y-4">
            {stepLabel("Répartition du contrat")}

            <div className="rounded-xl border bg-white/60 p-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold">
                    Répartir le montant du contrat
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Le montant du contrat ne couvre pas totalement tous les
                    besoins. Indiquez la part à affecter à chaque besoin.
                  </p>
                </div>

                <div className="text-right">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Montant du contrat (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {paidAmountNumber.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Total sélectionné (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {chosenDesiredTotalEUR.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>

                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Total alloué (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {allocationTotal.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>

                <div className="rounded-lg border bg-white p-3">
                  <div className="text-[0.7rem] uppercase tracking-wide text-slate-500">
                    Reste à répartir (EUR)
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {allocationRemaining.toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    €
                  </div>
                </div>
              </div>

              {allocationExceeds && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    Le total alloué dépasse le montant du contrat. Ajustez la
                    répartition pour continuer.
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {selectedDesiredFundings.map((df: any) => {
                  const id = Number(df?.id);
                  const title =
                    df?.financier?.fullname ||
                    df?.financer?.fullname ||
                    df?.title ||
                    df?.name ||
                    `Besoin #${id || "?"}`;

                  const amount = toNumber(df?.amount ?? 0);
                  const cur = readCurrencyLike(df, projectCurrency);

                  return (
                    <div
                      key={id || title}
                      className="rounded-lg border bg-white p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 break-words">
                            {title}
                          </div>
                          {df?.comment ? (
                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                              {df.comment}
                            </div>
                          ) : null}
                          <div className="text-xs text-muted-foreground mt-1">
                            Montant d’origine : {formatMoney(amount, cur)}
                          </div>
                          {desiredEurById[id] ? (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              ≈{" "}
                              {desiredEurById[id].toLocaleString("fr-FR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              €
                            </div>
                          ) : null}
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSelectedDesiredFundingIds((prev) =>
                              prev.filter((n) => Number(n) !== id),
                            )
                          }
                        >
                          Retirer
                        </Button>
                      </div>

                      <div className="mt-3 w-full md:w-48">
                        <Label className="text-xs">Montant alloué (€)</Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={desiredAllocations[id] ?? ""}
                          onChange={(e) =>
                            setDesiredAllocations((prev) => ({
                              ...prev,
                              [id]: e.target.value,
                            }))
                          }
                          placeholder="0,00"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {unselectedDesiredFundings.length > 0 && (
                <div className="rounded-lg border bg-white p-3">
                  <div className="text-sm font-semibold text-slate-900">
                    Ajouter rapidement un besoin
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sélectionnez un financement souhaité à ajouter à la
                    répartition.
                  </p>
                  <div className="mt-3 flex flex-col md:flex-row gap-2 md:items-center">
                    <Select
                      value={quickAddDesiredId}
                      onValueChange={setQuickAddDesiredId}
                    >
                      <SelectTrigger className="w-full md:max-w-sm">
                        <SelectValue placeholder="Choisir un financement souhaité" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {unselectedDesiredFundings.map((df: any) => {
                          const id = Number(df?.id);
                          const title =
                            df?.financier?.fullname ||
                            df?.financer?.fullname ||
                            df?.title ||
                            df?.name ||
                            `Besoin #${id || "?"}`;
                          const amount = toNumber(df?.amount ?? 0);
                          const cur = readCurrencyLike(df, projectCurrency);
                          return (
                            <SelectItem key={id || title} value={String(id)}>
                              {title} — {formatMoney(amount, cur)}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      onClick={() => {
                        const id = Number(quickAddDesiredId);
                        if (!id) return;
                        setSelectedDesiredFundingIds((prev) => {
                          const setIds = new Set(prev.map((n) => Number(n)));
                          setIds.add(id);
                          return Array.from(setIds);
                        });
                        setQuickAddDesiredId("");
                      }}
                      disabled={!quickAddDesiredId}
                    >
                      Ajouter
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="secondary" onClick={goPrev}>
                  Précédent
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Fermer
                </Button>
              </div>

              <Button
                onClick={goNext}
                disabled={allocationExceeds}
                title={
                  allocationExceeds
                    ? "Le total alloué dépasse le montant du contrat"
                    : "Continuer"
                }
              >
                Continuer
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ===================== STEP: Échéancier ===================== */}
        {step === 3 && (
          <div className="">
            {stepLabel("Échéancier de versements (optionnel)")}

            <div className="rounded-md border bg-white p-3 mb-3">
              <div className="text-sm font-medium">Montant du contrat</div>
              <div className="text-base mt-1">
                {paidAmountNumber.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                €
              </div>
              {contractAmountLocal != null && (
                <div className="text-xs text-muted-foreground mt-1">
                  ≈{" "}
                  {contractAmountLocal.toLocaleString("fr-FR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  {projectCurrencySymbol}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Versements prévus</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Ajoutez un échéancier si nécessaire. Les montants sont en euros et
              seront envoyés tels quels.
            </p>
            <div className="my-3 rounded-md border border-sky-200 bg-sky-50 p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
              <div className="text-sm text-sky-800">
                Cette étape est optionnelle. Elle sert à renseigner un
                échéancier prévisionnel du contrat (dates et montants prévus),
                et non les versements déjà reçus. Vous pourrez le modifier plus
                tard si nécessaire.
              </div>
            </div>

            <div className="space-y-3">
              {expectedPaids.map((row) => (
                <div
                  key={row.id}
                  className="grid grid-cols-1 md:grid-cols-[1fr_220px_80px] gap-3 items-center rounded-md border px-3 py-2 bg-white"
                >
                  <div>
                    <Label className="text-xs">Montant (€)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={row.amount}
                      onChange={(e) =>
                        updateLine(row.id, {
                          amount: Number(e.target.value || 0),
                        })
                      }
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input
                      type="date"
                      value={row.date}
                      onChange={(e) =>
                        updateLine(row.id, { date: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2 justify-end pt-5 md:pt-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(row.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <div className="flex justify-center">
                <Button variant="outline" onClick={addLine}>
                  <Plus className="w-4 h-4 mr-2" /> Ajouter une ligne
                </Button>
              </div>
            </div>

            <DialogFooter className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="secondary" onClick={goPrev}>
                  Précédent
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Fermer
                </Button>
              </div>
              <Button onClick={save} disabled={saving}>
                {saving ? "Enregistrement..." : "Créer le financement obtenu"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>

      <AddFunderDialog
        hasTrigger={false}
        open={openAddFunderModal}
        onHandleOpen={setOpenAddFunderModal}
        onSaveSuccess={(res: Funder) => {
          setFunders((prev) => {
            const exists = prev.some((f) => Number(f.id) === Number(res.id));
            if (exists) {
              return prev.map((f) =>
                Number(f.id) === Number(res.id)
                  ? { ...res, representants: res.representants ?? [] }
                  : f,
              );
            }
            return [
              ...prev,
              { ...res, representants: res.representants ?? [] },
            ];
          });
          setFunderId(String(res.id));
          setRepresentantId("");
          setOpenAddFunderModal(false);
        }}
      />

      <ContactAdd
        open={openAddRepresentantModal}
        setOpen={setOpenAddRepresentantModal}
        financer={funderId ? Number(funderId) : undefined}
        handleCreate={(newRepresentant: {
          id: number;
          firstname: string;
          lastname: string;
        }) => {
          if (!funderId) {
            setOpenAddRepresentantModal(false);
            return;
          }
          const selectedFunderId = Number(funderId);
          setFunders((prev) =>
            prev.map((f) => {
              if (Number(f.id) !== selectedFunderId) return f;
              const reps = Array.isArray(f.representants)
                ? f.representants
                : [];
              const exists = reps.some(
                (r) => Number(r.id) === Number(newRepresentant.id),
              );
              return exists
                ? f
                : { ...f, representants: [...reps, newRepresentant] };
            }),
          );
          setRepresentantId(String(newRepresentant.id));
          setOpenAddRepresentantModal(false);
        }}
      />

      {/* ====== POPUP Upload PJ ====== */}
      <Dialog open={pjOpen} onOpenChange={(o) => !isUploading && setPjOpen(o)}>
        <DialogContent className="max-w-lg">
          <div className="space-y-4">
            <h3 className="text-base font-semibold">
              Ajouter une pièce jointe
            </h3>

            <div>
              <Label className="text-sm font-medium">Type de document</Label>
              <DocumentTypeCombobox value={pjType} onChange={setPjType} />
            </div>

            <div>
              <Label className="text-sm font-medium">Commentaire</Label>
              <textarea
                value={pjComment}
                onChange={(e) => setPjComment(e.target.value)}
                className="w-full border p-2 rounded"
                placeholder="Ajouter un commentaire (facultatif)"
                disabled={isUploading}
              />
            </div>

            <div className="space-y-2">
              <FileDropzone
                accept="PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG"
                onFileSelected={setPjFiles}
              />
              {pjFiles?.length > 0 && (
                <ul className="text-xs text-gray-700 space-y-1 max-h-28 overflow-auto">
                  {pjFiles.map((f, idx) => (
                    <li
                      key={idx}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        className="border rounded px-1 py-0.5 hover:bg-gray-50"
                        onClick={() => removeSelectedPjFile(idx)}
                        disabled={isUploading}
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                className="px-3 py-2 rounded border text-sm"
                variant="outline"
                onClick={() => {
                  if (isUploading) return;
                  setPjOpen(false);
                }}
                disabled={isUploading}
              >
                Fermer
              </Button>
              <Button
                className="px-3 py-2 rounded text-sm disabled:opacity-60 inline-flex items-center gap-2"
                onClick={handleUploadPj}
                disabled={isUploading}
              >
                {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/** ===================== Utils ===================== */

const extractProjectsFromPrograms = (programs: any): any[] => {
  if (!Array.isArray(programs)) return [];

  const seen = new Set<number>();
  const projects: any[] = [];

  for (const program of programs) {
    const programRef = {
      id: Number(program?.id),
      name: program?.name || "",
    };

    const programProjects = Array.isArray(program?.projects)
      ? program.projects
      : [];
    for (const project of programProjects) {
      const id = Number(project?.id);
      if (!id || Number.isNaN(id) || seen.has(id)) continue;
      seen.add(id);

      projects.push({
        ...project,
        program: project?.program ?? programRef,
        realFundings: Array.isArray(project?.realFundings)
          ? project.realFundings
          : [],
        desiredFunding: Array.isArray(project?.desiredFunding)
          ? project.desiredFunding
          : [],
      });
    }
  }

  return projects.sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "fr"),
  );
};

const extractProjectByIdFromFundingResponse = (
  res: any,
  projectId: number,
): any | null => {
  const targetId = Number(projectId);
  if (!targetId || Number.isNaN(targetId)) return null;

  const candidates: any[][] = [];
  if (Array.isArray(res)) candidates.push(res);
  if (Array.isArray(res?.data)) candidates.push(res.data);
  if (Array.isArray(res?.projects)) candidates.push(res.projects);

  for (const list of candidates) {
    const match = list.find(
      (item) =>
        item && typeof item === "object" && Number(item?.id) === targetId,
    );
    if (match) return match;
  }

  if (Number(res?.id) === targetId) return res;
  return null;
};

const extractIndicatorsFromResponse = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const formatData = (data: any[]) => {
  return data.map((d) => {
    return {
      ...d,
      realFundings: (d.realFundings || []).map((rf: any) => {
        return {
          ...rf,
          representant: rf.verdeo
            ? {
                firstname: "Verdeo",
                lastname: "",
              }
            : rf.representant,
        };
      }),
    };
  });
};
